import React, { useState } from 'react';
import BRAND from '../brandConfig';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import { fmtMoney } from '../lib/format';
import { vendorSplit, withCardFee, RATES } from '../lib/pricing';
import { PAYMENT_MODES, CREDENTIAL_TYPES, LEDGER_STATUSES, credentialLabel, balanceOf, neverExpires } from '../lib/ledger';
import MessageThread from '../components/MessageThread';
import ItemEditor from '../components/ItemEditor';
import DocExtras from '../components/DocExtras';
import DocSheet from '../components/DocSheet';
import DocEditor from '../components/DocEditor';
import ClientEditor from '../components/ClientEditor';
import MediaStrip from '../components/MediaStrip';
import RequestItemEditor from '../components/RequestItemEditor';
import { SERVICE_TYPES } from './RequestForm';
import { QUALIFY_QUESTIONS } from '../lib/qualifyQuestions';
import { TAXONOMY } from './VendorDashboard';

// Lead sources the admin can pick when logging a lead manually.
const LEAD_SOURCES = ['Facebook', 'Nextdoor', 'Instagram', 'TikTok', 'Referral', 'Phone/Text', 'Google', 'Yelp', 'Website', 'Other'];

// All bottom-sheet modals (estimate, assign vendor, completion, invoice) plus
// their click-away overlays. Which one shows is driven by the `modal` state.
export default function Modals() {
  const {
    modal, setModal, activeRequest,
    estimateForm, setEstimateForm, handleSubmitEstimate,
    estimateForClient, setEstimateForClient, askDeleteEstimate,
    clientInvoiceForm, setClientInvoiceForm, handleSaveClientInvoice, isSavingInvoice,
    noteEdit, setNoteEdit, handleSaveNote, isSavingNote,
    assignVendorId, setAssignVendorId, vendors, handleAssignVendor,
    completionFiles, setCompletionFiles, completionNote, setCompletionNote, handleSubmitCompletion,
    invoiceForm, setInvoiceForm, invoiceTotal, handleSubmitInvoice,
    addLeadForm, setAddLeadForm, handleAddLead, isAddingLead, editingLeadItem, setEditingLeadItem, uploadPhotos,
    addClientForm, setAddClientForm, handleAddClient, isSavingClient,
    jobCustomerForm, setJobCustomerForm, handleSaveJobCustomer, isSavingJobCustomer, lastClientLink, setLastClientLink,
    approvedOpened, setApproved,
    leadQualify, setLeadQualify, saveLeadQualify, isSavingLead,
    openJobCustomer,
    openClientForm,
    appointmentInput, setAppointmentInput, handleSetAppointment,
    passwordForm, setPasswordForm, handleChangePassword,
    accountForm, setAccountForm, handleCreateAccount,
    accountMemberForm, setAccountMemberForm, handleSaveAccountMember,
    adminPropertyForm, setAdminPropertyForm, handleCreateAdminProperty,
    propertyJobForm, setPropertyJobForm, handleCreatePropertyJob,
    lastInviteLink, setLastInviteLink, showToast,
    emailForm, setEmailForm, handleChangeEmail,
    confirmDelete, setConfirmDelete, handleConfirmDelete,
    currentUser, requests, activeEntry, isSavingLedger,
    ledgerForm, setLedgerForm, ledgerDocFiles, setLedgerDocFiles, handleSaveLedgerEntry,
    paymentForm, setPaymentForm, paymentBlock, handleRecordPayment,
    progressForm, setProgressForm, progressFiles, setProgressFiles, handleAddProgress,
    credentialForm, setCredentialForm, credentialFile, setCredentialFile, handleSaveCredential,
    clients, projects, activeProject, isSavingProject,
    projectForm, setProjectForm, handleSaveProject,
    lineForm, setLineForm, handleSaveProjectLine,
    clientPayForm, setClientPayForm, handleSaveClientPayment,
    expenseForm, setExpenseForm, receiptFile, setReceiptFile, handleSaveExpense,
    importForm, setImportForm, importBusy, runImportPreview, confirmImport,
    estimateForm2, setEstimateForm2, estimateItemsTotal, handleSubmitTechEstimate,
    invoiceForm2, setInvoiceForm2, invoiceItemsTotal, handleSaveTechInvoice,
    licenseForm, setLicenseForm, handleSaveLicenseCheck, isSubmittingApp,
    handleSaveTechEstimate,
    vendorEditForm, setVendorEditForm, handleSaveVendorEdit, isSavingVendor,
    lastVendorLink, setLastVendorLink,
    admins, adminForm, setAdminForm, handleAddAdmin, isSavingAdmin,
    signInLink, setSignInLink, handleSendSignInLink,
    docClientForm, setDocClientForm, openDocClient, saveDocClient, pickDocClient, vendorClients, setDocDate,
    toggleAdminPermission,
  } = useApp();
  const { t, optLabel } = useI18n(); // tech-facing modals translate; admin modals stay English

  const setLead = (patch) => setAddLeadForm(p => ({ ...p, ...patch }));
  const setLedger = (patch) => setLedgerForm(p => ({ ...p, ...patch }));
  const setPay = (patch) => setPaymentForm(p => ({ ...p, ...patch }));
  const setCred = (patch) => setCredentialForm(p => ({ ...p, ...patch }));
  const isAdmin = currentUser?.role === 'admin';
  const setProj = (patch) => setProjectForm(p => ({ ...p, ...patch }));
  const setLine = (patch) => setLineForm(p => ({ ...p, ...patch }));
  const setCPay = (patch) => setClientPayForm(p => ({ ...p, ...patch }));
  const setExp = (patch) => setExpenseForm(p => ({ ...p, ...patch }));
  const setEst = (patch) => setEstimateForm2(p => ({ ...p, ...patch }));
  const setInv2 = (patch) => setInvoiceForm2(p => ({ ...p, ...patch }));

  // The jobs on the request being priced, as Julian reads them on the estimate
  // sheet. A request from before line items existed has none, so its
  // description stands in as a single job — he can still read and fix it.
  const requestNotes = activeRequest
    ? (activeRequest.items?.length
        ? activeRequest.items
        : (activeRequest.description ? [{ title: activeRequest.serviceType, description: activeRequest.description, descriptionOriginal: activeRequest.descriptionOriginal, photos: activeRequest.photos }] : []))
    : [];
  // Whether this sheet is writing an estimate or correcting one that exists —
  // the same form either way, but it should not call itself "Submit" when the
  // customer already has the thing in their portal.
  const hasEstimate = activeRequest?.estimate?.amount != null;
  const setVendorEdit = (patch) => setVendorEditForm(p => ({ ...p, ...patch }));
  // Index of the line currently open in its own editor screen, or null. One
  // per document, because an estimate and an invoice can both be part-written.
  const [editingItem, setEditingItem] = useState(null);
  const [editingInvoiceItem, setEditingInvoiceItem] = useState(null);
  const sel = { padding: '11px 13px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--white)', color: 'var(--navy)', fontFamily: 'inherit', width: '100%' };

  return (
    <>
      {/* ── Estimate ───────────────────────────────────────────────────────
          Joist's Edit Estimate, on a full screen — see components/DocEditor.js.
          What the customer asked for sits above the lines, because pricing a
          job called "DRYWALL" from its title alone means leaving the sheet to
          read the note and the photos, and a sheet you have to leave to use is
          a sheet that gets guessed at. */}
      {modal === 'estimate' && (
        <DocEditor
          title={estimateForClient ? 'New Estimate' : hasEstimate ? 'Edit Estimate' : 'New Estimate'}
          clientName={estimateForClient
            ? estimateForClient.client.name
            : (activeRequest?.fullName || activeRequest?.homeownerId?.name || 'Client')}
          clientSub={estimateForClient
            ? [estimateForClient.client.phone,
               [estimateForClient.client.address, estimateForClient.client.city].filter(Boolean).join(', ')]
              .filter(Boolean).join(' · ')
            : [activeRequest?.serviceType, activeRequest?.serviceAddress].filter(Boolean).join(' · ')}
          onEditClient={activeRequest ? () => openJobCustomer(activeRequest) : undefined}
          banner={activeRequest?.estimateStatus === 'approved' ? (
            /* Said plainly, because it is the one surprising thing about
               editing: a changed estimate is a new offer, and the customer has
               to agree to the new one. */
            <div className="alert alert-amber" style={{ margin: 16 }}>
              This estimate has been approved. Saving changes sends it back to the customer as a new estimate to approve.
            </div>
          ) : null}
          header={
            <>
              {/* ── THE DATE ON THE PAPER ────────────────────────────────
                  Julian walks a property on Monday and prices it on
                  Wednesday. The customer is reading a document about
                  Monday's visit, so the date is his to set.

                  It does NOT move when the estimate was sent. That is a
                  fact about the app — what the board sorts by, what an
                  approval is measured against — and backdating a document
                  must not quietly reorder his work. */}
              <div className="de-fields">
                <div className="form-row" style={{ marginTop: 12 }}>
                  <div className="form-group">
                    <label>Estimate #</label>
                    <input
                      type="text"
                      value={estimateForm.number || ''}
                      placeholder="Assigned for you"
                      onChange={e => setEstimateForm(p => ({ ...p, number: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date on this estimate</label>
                    <input
                      type="date"
                      value={estimateForm.docDate || ''}
                      onChange={e => setEstimateForm(p => ({ ...p, docDate: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', margin: '-4px 0 10px', lineHeight: 1.5 }}>
                  Leave the number blank and the next one is assigned, carrying on from the
                  last you used. Set the date back to the day you walked the property.
                </div>
              </div>

              {requestNotes.length > 0 && (
                <>
                  <div className="de-sec">What they asked for</div>
                  <div className="de-fields">
                  {requestNotes.map((it, i) => {
                    const editing = noteEdit?.index === i;
                    return (
                      <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 8, background: editing ? '#fffef9' : 'var(--bg)' }}>
                        {editing ? (
                          <>
                            <input
                              className="li-input" style={{ width: '100%', marginBottom: 8 }}
                              value={noteEdit.title}
                              placeholder="What needs fixing"
                              onChange={e => setNoteEdit(p => ({ ...p, title: e.target.value }))}
                            />
                            <textarea
                              value={noteEdit.description}
                              placeholder="The note for this job…"
                              style={{ width: '100%', minHeight: 96, marginBottom: 8 }}
                              onChange={e => setNoteEdit(p => ({ ...p, description: e.target.value }))}
                            />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="btn btn-primary btn-sm" disabled={isSavingNote} onClick={handleSaveNote}>{isSavingNote ? 'Saving…' : 'Save note'}</button>
                              <button className="btn btn-ghost btn-sm" onClick={() => setNoteEdit(null)}>Cancel</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--navy)' }}>
                                {requestNotes.length > 1 && <span style={{ color: 'var(--muted)', fontWeight: 400 }}>{i + 1}. </span>}
                                {it.title || it.serviceType || 'This job'}
                              </div>
                              <button className="btn btn-ghost btn-sm" onClick={() => setNoteEdit({ index: i, title: it.title || '', description: it.description || '' })}>Edit</button>
                            </div>
                            {it.description && it.description !== it.title && (
                              <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.45, marginTop: 4, whiteSpace: 'pre-wrap' }}>{it.description}</div>
                            )}
                            {/* Shown only once he has changed something, and never
                                thrown away: the estimate is approved against what
                                the customer asked for, not against our tidy-up. */}
                            {it.descriptionOriginal && it.descriptionOriginal !== it.description && (
                              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--border)', whiteSpace: 'pre-wrap' }}>
                                <strong>Their words:</strong> {it.descriptionOriginal}
                              </div>
                            )}
                            <MediaStrip urls={it.photos} label="Photos" />
                          </>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ height: 14 }}></div>
                  </div>
                </>
              )}
            </>
          }
          lines={estimateForm.breakdown}
          onChangeLines={(breakdown) => setEstimateForm(p => ({ ...p, breakdown }))}
          notes={estimateForm.notes}
          onChangeNotes={(notes) => setEstimateForm(p => ({ ...p, notes }))}
          totalNote={(() => {
            const base = (estimateForm.breakdown || []).reduce((t2, r) => t2 + (parseFloat(r.cost) || 0), 0);
            if (base <= 0) return null;
            const client = withCardFee(base);
            return (
              <div style={{ fontSize: 12.5, color: 'var(--muted)', padding: '0 16px 8px', textAlign: 'right' }}>
                Customer sees {fmtMoney(client)} (card fee of {fmtMoney(client - base)} built in, no fee line)
              </div>
            );
          })()}
          doneLabel={estimateForClient ? 'Create Job & Send Estimate' : hasEstimate ? 'Save & Send Updated Estimate' : 'Send Estimate to Homeowner'}
          onDone={handleSubmitEstimate}
          // Only on an estimate that has been written and sent — there is
          // nothing to delete on one being typed for the first time.
          onDelete={activeRequest?.estimate?.submittedAt && askDeleteEstimate
            ? () => { setModal(null); askDeleteEstimate(activeRequest); }
            : undefined}
          deleteLabel="Delete Estimate"
          backLabel="Estimate"
          onCancel={() => { setModal(null); setEstimateForClient(null); setNoteEdit(null); }}
        />
      )}

      {/* Assign Vendor Modal */}
      <div className={`modal ${modal === 'assign' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">Assign Technician</div>
          {activeRequest && <div className="qps-sheet-sub">{activeRequest.serviceType}</div>}

          {/* Who the company asked for, said before the list rather than
              after it. They cannot see who is booked out on Tuesday, so it is
              a request and not an instruction — but it is the first thing to
              know while choosing, not a detail to discover afterwards. */}
          {activeRequest?.requestedVendorName && (
            <div className="alert alert-amber" style={{ marginBottom: 16 }}>
              They asked for <strong>{activeRequest.requestedVendorName}</strong>.
            </div>
          )}

          <div className="form-group">
            <label>Select Technician</label>
            <select value={assignVendorId} onChange={e => setAssignVendorId(e.target.value)}>
              <option value="">Choose a technician…</option>
              {vendors.map(v => (
                <option key={v._id} value={v._id}>{v.businessName}{v.specialty ? ` — ${v.specialty}` : ''}</option>
              ))}
            </select>
          </div>

          {vendors.length === 0 && <div className="alert alert-amber">No technicians registered yet. They need to create an account first.</div>}

          {/* Sending somebody work we cannot pay them for. Not a block —
              Julian dispatches by phone and the man sets Stripe up that
              evening — but he should know before the job is on their
              screen, not when the homeowner's card payment has nowhere to
              land. Their own estimates and invoices are free and need
              none of this; this is only QPS work. */}
          {(() => {
            const picked = vendors.find(v => String(v._id) === String(assignVendorId));
            if (!picked || picked.chargesEnabled) return null;
            return (
              <div className="alert alert-amber" style={{ marginBottom: 14, flexDirection: 'column', alignItems: 'flex-start' }}>
                <strong>{picked.businessName} has no bank connected yet.</strong>
                <span style={{ fontSize: 12.5, lineHeight: 1.55 }}>
                  Assign them anyway if you want — the job shows on their phone with a band
                  telling them to set it up, and it takes about five minutes. Until they do, a
                  card payment from the homeowner has nowhere to go.
                </span>
              </div>
            );
          })()}

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} onClick={handleAssignVendor} disabled={!assignVendorId}>Assign Technician</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'assign' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Completion Photos Modal */}
      <div className={`modal ${modal === 'completion' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">{t('m.submitCompletion')}</div>
          {activeRequest && <div className="qps-sheet-sub">{optLabel(activeRequest.serviceType)}</div>}

          <div className="form-group">
            <label>{t('m.completionPhotos')} <span className="req">*</span></label>
            <div className="upload-zone" onClick={() => document.getElementById('completion-input').click()} style={{ cursor: 'pointer' }}>
              <input id="completion-input" type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={e => setCompletionFiles(Array.from(e.target.files))} />
              {completionFiles.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                  {completionFiles.map((f, i) => <span key={i} className="file-chip">{f.name}</span>)}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}><strong style={{ color: 'var(--blue)' }}>{t('req.tapToUpload')}</strong> {t('m.beforeAfter')}</p>
              )}
            </div>
          </div>
          <div className="form-group"><label>{t('m.completionNotes')}</label><textarea value={completionNote} onChange={e => setCompletionNote(e.target.value)} placeholder={t('m.describeDone')} /></div>

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} onClick={handleSubmitCompletion}>{t('m.submitCompletion')}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'completion' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Invoice Modal */}
      <div className={`modal ${modal === 'invoice' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          {/* The tech is submitting his work for billing; Julian is billing a
              management company. Same record, and it reads wrong to both if it
              only ever says one of them. */}
          <div className="qps-sheet-title">
            {currentUser?.role === 'admin' ? 'Invoice' : t('vd.submitInvoice')}
          </div>
          {activeRequest && (
            <div className="qps-sheet-sub">
              {[optLabel(activeRequest.serviceType), activeRequest.unitLabel, activeRequest.serviceAddress]
                .filter(Boolean).join(' · ')}
            </div>
          )}

          {/* ── WHERE THIS BILL IS GOING ────────────────────────────────
              This block used to be admin only, so a technician raising an
              invoice never saw who it was for or what address it would reach.
              Sam Skanchy's first invoice went to not-provided@lead.local, the
              stand-in this app writes when a lead is typed in without an
              email, and the screen said Sent. Everybody who presses Send now
              sees the address first, and is asked for one when there is
              none. */}
          {activeRequest && (() => {
            const current = String(activeRequest.email || '');
            const real = /^[^@\s,;<>]+@[^@.\s,;<>]+(\.[^@.\s,;<>]+)+$/.test(current)
              && !['local', 'localhost', 'invalid', 'test', 'example']
                .includes(current.toLowerCase().split('@').pop().split('.').pop());
            return (
              <>
                <div className="sec-label">{real ? 'This bill goes to' : 'Where should this bill go?'}</div>
                <div className="de-client" style={{ borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginBottom: real ? 14 : 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="de-client-name">{activeRequest.fullName || 'No name on this job'}</div>
                    <div className="de-client-sub">
                      {real
                        ? [current, activeRequest.phone].filter(Boolean).join(' · ')
                        : <span style={{ color: 'var(--red)', fontWeight: 700 }}>No email on this job</span>}
                    </div>
                    <div className="de-client-sub">
                      {[activeRequest.serviceAddress, activeRequest.city, activeRequest.state, activeRequest.zip]
                        .filter(Boolean).join(', ') || 'No address on this job'}
                    </div>
                  </div>
                  {currentUser?.role === 'admin' && (
                    <button type="button" className="de-mini" onClick={() => openJobCustomer(activeRequest)}>Edit</button>
                  )}
                </div>
                {!real && (
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label>Customer&rsquo;s email address</label>
                    <input
                      type="email" inputMode="email" autoCapitalize="off" autoCorrect="off"
                      placeholder="name@example.com"
                      value={invoiceForm.customerEmail || ''}
                      onChange={e => setInvoiceForm(p => ({ ...p, customerEmail: e.target.value }))}
                    />
                    <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 5 }}>
                      Without this the bill cannot reach them. You will still get a
                      link you can text.
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          <div className="sec-label">{t('m.lineItems')}</div>
          {invoiceForm.lineItems.map((row, i) => (
            <div key={i} className="inv-line-row">
              <input className="li-input" placeholder={t('m.description')} value={row.description} onChange={e => {
                const li = [...invoiceForm.lineItems];
                li[i] = { ...li[i], description: e.target.value };
                setInvoiceForm(p => ({ ...p, lineItems: li }));
              }} />
              <input className="li-input" placeholder={t('m.qty')} type="number" value={row.qty} onChange={e => {
                const li = [...invoiceForm.lineItems];
                const qty = parseFloat(e.target.value) || 0;
                const uc = parseFloat(li[i].unitCost) || 0;
                li[i] = { ...li[i], qty: e.target.value, total: (qty * uc).toFixed(2) };
                setInvoiceForm(p => ({ ...p, lineItems: li }));
              }} />
              <input className="li-input" placeholder={t('m.unit')} type="number" value={row.unitCost} onChange={e => {
                const li = [...invoiceForm.lineItems];
                const uc = parseFloat(e.target.value) || 0;
                const qty = parseFloat(li[i].qty) || 0;
                li[i] = { ...li[i], unitCost: e.target.value, total: (qty * uc).toFixed(2) };
                setInvoiceForm(p => ({ ...p, lineItems: li }));
              }} />
              <input className="li-input" placeholder={t('m.total')} value={row.total} readOnly style={{ background: 'var(--bg)' }} />
              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => {
                const li = invoiceForm.lineItems.filter((_, idx) => idx !== i);
                setInvoiceForm(p => ({ ...p, lineItems: li.length ? li : [{ description: '', qty: 1, unitCost: '', total: '' }] }));
              }}>×</button>
            </div>
          ))}
          <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => setInvoiceForm(p => ({ ...p, lineItems: [...p.lineItems, { description: '', qty: 1, unitCost: '', total: '' }] }))}>{t('m.addLine')}</button>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid var(--border)', marginBottom: 16, fontSize: 16, fontWeight: 800 }}>
            <span>{currentUser?.role === 'vendor' ? 'Your price' : t('m.invoiceTotal')}</span>
            <span style={{ color: 'var(--green)' }}>{fmtMoney(invoiceTotal)}</span>
          </div>

          {/* What the customer will be asked for, and where it goes. The
              technician sees every line of it: they agreed to the platform fee
              and it is their money. The customer sees only the one total. */}
          {currentUser?.role === 'vendor' && invoiceTotal > 0 && (() => {
            const sp = vendorSplit(invoiceTotal);
            const row = (label, v, strong) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontWeight: strong ? 800 : 400 }}>
                <span>{label}</span><span>{fmtMoney(v)}</span>
              </div>
            );
            return (
              <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-xs)', padding: '10px 12px', marginBottom: 16, fontSize: 13 }}>
                {row(`QPS Hub platform fee (${RATES.platformPct}%)`, sp.platformFee)}
                {row('Card / bank processing', sp.processingFee)}
                {row('Customer pays', sp.clientTotal, true)}
                <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 6 }}>
                  When the customer pays online you receive {fmtMoney(sp.vendorPrice)}, your full price. The customer sees one total.
                </div>
              </div>
            );
          })()}

          <div className="form-group"><label>{t('m.notes')}</label><textarea value={invoiceForm.notes} onChange={e => setInvoiceForm(p => ({ ...p, notes: e.target.value }))} placeholder={t('m.anyNotes')} /></div>

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} onClick={handleSubmitInvoice}>
            {currentUser?.role === 'admin' ? 'Send Invoice' : t('vd.submitInvoice')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'invoice' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Add Lead Modal (admin logs a lead from Facebook, referral, phone, etc.) */}
      <div className={`modal ${modal === 'addLead' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">Add a Lead</div>
          <div className="qps-sheet-sub">Log a lead from Facebook, Nextdoor, a referral, or a phone call.</div>

          <div className="form-row">
            <div className="form-group"><label>Full Name <span className="req">*</span></label><input type="text" value={addLeadForm.fullName} onChange={e => setLead({ fullName: e.target.value })} placeholder="Jane Doe" /></div>
            <div className="form-group"><label>Phone <span className="req">*</span></label><input type="tel" value={addLeadForm.phone} onChange={e => setLead({ phone: e.target.value })} placeholder="(801) 555-0100" /></div>
          </div>
          <div className="form-group"><label>Email</label><input type="email" value={addLeadForm.email} onChange={e => setLead({ email: e.target.value })} placeholder="Optional" /></div>

          <div className="form-group"><label>City</label>
            <select value={addLeadForm.city} onChange={e => setLead({ city: e.target.value })}>
              <option value="">Select city…</option>
              {BRAND.serviceArea.primaryCities.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="Other">Other / outside area</option>
            </select>
          </div>
          {addLeadForm.city === 'Other' && (
            <div className="form-group"><label>Which city?</label><input type="text" value={addLeadForm.cityOther} onChange={e => setLead({ cityOther: e.target.value })} placeholder="Enter city" /></div>
          )}
          <div className="form-group"><label>Service Address</label><input type="text" value={addLeadForm.serviceAddress} onChange={e => setLead({ serviceAddress: e.target.value })} placeholder="Street address (optional)" /></div>

          <div className="form-row">
            <div className="form-group"><label>Lead Source</label>
              <select value={addLeadForm.leadSource} onChange={e => setLead({ leadSource: e.target.value })}>
                {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Urgency</label>
              <select value={addLeadForm.urgency} onChange={e => setLead({ urgency: e.target.value })}>
                <option>Standard – 1 week</option><option>Soon – 2-3 days</option><option>Urgent – 24 hrs</option>
              </select>
            </div>
          </div>

          <div className="form-group"><label>Service Type</label>
            <select value={addLeadForm.serviceType} onChange={e => setLead({ serviceType: e.target.value })}>
              <option value="">Select…</option>
              <option>Handyman – General Repair</option>
              <option>Roofing</option>
              <option>Siding</option>
              <option>Storm Damage</option>
              <option>Remodel – Kitchen</option>
              <option>Remodel – Bathroom</option>
              <option>Basement Finish</option>
              <option>Other</option>
            </select>
          </div>
          {/* What they need, a line per job — the same list the website form
              collects and the same one the text conversation builds, on the
              same editor screen. Julian on the phone hears "the faucet, and
              the gate, and the drywall" as often as anyone, and a lead that
              holds them separately is a lead he can price line by line
              without re-reading a paragraph. */}
          <div className="sec-label">What do they need?</div>
          <div className="help-line">
            A line per job. Tap one to write the note and add photos.
          </div>
          {(addLeadForm.items || []).map((it, i) => {
            const shots = (it.photos || []).length;
            return (
              <button type="button" key={i} className="item-row" onClick={() => setEditingLeadItem(i)}>
                <span style={{ minWidth: 0 }}>
                  <span className="item-row-t" style={{ display: 'block' }}>{it.title || it.description || 'New job'}</span>
                  <span className="item-row-s" style={{ display: 'block' }}>
                    {it.serviceType || addLeadForm.serviceType || 'Tap to fill this in'}
                    {shots > 0 && ` · ${shots} photo${shots === 1 ? '' : 's'}`}
                  </span>
                </span>
                <span className="item-row-amt" style={{ color: 'var(--blue)' }}>›</span>
              </button>
            );
          })}
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => {
            const items = [...(addLeadForm.items || []), { title: '', serviceType: '', description: '', photos: [] }];
            setLead({ items });
            setEditingLeadItem(items.length - 1);
          }}>+ Add another job</button>

          <div className="form-group"><label>Appointment (in-person estimate)</label><input type="datetime-local" value={addLeadForm.appointmentAt} onChange={e => setLead({ appointmentAt: e.target.value })} /></div>

          {/* Disabled while it saves. Without this a second tap on a slow
              phone wrote a second lead, and two cards for one job is two
              estimates and a customer billed twice. */}
          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }}
            onClick={handleAddLead} disabled={isAddingLead}>
            {isAddingLead ? 'Adding…' : 'Add Lead'}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'addLead' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* One job on the lead, on its own screen — the same component the
          customer's request form uses. The difference is `onUploadFiles`:
          Julian is signed in, so his photos go straight to Cloudinary as he
          picks them, the way a technician's estimate lines already work. */}
      {modal === 'addLead' && editingLeadItem !== null && addLeadForm.items?.[editingLeadItem] && (
        <RequestItemEditor
          item={addLeadForm.items[editingLeadItem]}
          index={editingLeadItem}
          total={addLeadForm.items.length}
          serviceTypes={SERVICE_TYPES}
          onUploadFiles={uploadPhotos}
          onChange={(next) => {
            const items = [...addLeadForm.items];
            items[editingLeadItem] = next;
            setLead({ items });
          }}
          onDelete={() => {
            const items = addLeadForm.items.filter((_, i) => i !== editingLeadItem);
            setLead({ items: items.length ? items : [{ title: '', serviceType: '', description: '', photos: [] }] });
            setEditingLeadItem(null);
          }}
          onClose={() => setEditingLeadItem(null)}
        />
      )}

      {/* ── Add a Client ───────────────────────────────────────────────────
          The website only ever creates a client as a by-product of somebody
          filing a request. This is for the customer Julian already has: taken
          down on the phone, with the address the work is at and the address the
          invoice goes to — the second of which had nowhere to live before.

          A full screen rather than a bottom sheet, laid out like Joist's Edit
          Client — see components/ClientEditor.js. */}
      {modal === 'addClient' && addClientForm && (
        <ClientEditor
          form={addClientForm}
          setForm={setAddClientForm}
          onSave={handleAddClient}
          saving={isSavingClient}
          clients={clients}
          onPickClient={openClientForm}
          link={lastClientLink}
          onCopyLink={() => { navigator.clipboard?.writeText(lastClientLink); showToast('Link copied'); }}
          onClose={() => { setModal(null); setAddClientForm(null); setLastClientLink(''); }}
        />
      )}

      {/* ── Who a technician's own estimate or invoice is for ──────────────
          The same client screen, opened from the document rather than from the
          client list. It renders OUTSIDE the modal switch on purpose: it sits
          on top of the estimate sheet, and closing it has to land back on that
          sheet with every line still typed into it. */}
      {docClientForm && (
        <ClientEditor
          className="dc-modal"
          form={docClientForm}
          setForm={setDocClientForm}
          onSave={saveDocClient}
          clients={vendorClients}
          onPickClient={pickDocClient}
          backLabel={docClientForm.kind === 'invoice' ? 'Invoice' : 'Estimate'}
          title="Who is this for?"
          onClose={() => setDocClientForm(null)}
        />
      )}

      {/* ── Who the job is for ─────────────────────────────────────────────
          The same screen as the client list, opened on a job. Correcting a
          customer's email here is the difference between an invoice that
          reaches them and one that goes nowhere. */}
      {modal === 'jobCustomer' && jobCustomerForm && (
        <ClientEditor
          form={jobCustomerForm}
          setForm={setJobCustomerForm}
          onSave={handleSaveJobCustomer}
          saving={isSavingJobCustomer}
          backLabel="Job"
          title="Edit Customer"
          onClose={() => { setModal(null); setJobCustomerForm(null); }}
        />
      )}

      {/* ── Invoice a client ───────────────────────────────────────────────
          The same full screen the estimate is written on (components/
          DocEditor.js) — because an invoice is the same act twice: the
          estimate says what the work will cost, the invoice says what it did,
          and both are a client, a set of described lines, and a total.

          Both ways in land here: Create Invoice on a client's page, and the
          Invoice button on one of their estimates, which arrives with the
          agreed lines already on it. */}
      {modal === 'clientInvoice' && clientInvoiceForm && (() => {
        const f = clientInvoiceForm;
        const setInv = (patch) => setClientInvoiceForm(p => ({ ...p, ...patch }));
        // Only this client's projects: an invoice on somebody else's statement
        // is a mistake nobody catches until they read the bill.
        const theirs = (projects || []).filter(p => String(p.homeownerId?._id || p.homeownerId) === String(f.client._id));
        return (
          <DocEditor
            title={f.requestId ? 'Invoice This Estimate' : 'New Invoice'}
            clientName={f.client.name}
            clientSub={[
              f.client.phone,
              [f.client.address, f.client.city].filter(Boolean).join(', '),
              f.requestId ? 'Carried over from the estimate they agreed' : '',
            ].filter(Boolean).join(' · ')}
            header={
              <div className="de-fields">
                <div className="form-group"><label>Which project</label>
                  <select value={f.projectId} onChange={e => setInv({ projectId: e.target.value })}>
                    <option value="">A new project</option>
                    {theirs.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                  </select>
                </div>
                {!f.projectId && (
                  <div className="form-group"><label>Project name <span className="req">*</span></label>
                    <input type="text" value={f.title} onChange={e => setInv({ title: e.target.value })}
                      placeholder="Back gate" /></div>
                )}

                {/* ── SECOND PROJECT FOR THE SAME CLIENT ───────────────────
                    Julian billed nine trades of one basement onto Julie
                    Tait's statement one estimate at a time. The dropdown
                    says "A new project" every time it opens, so the second
                    one quietly became a project of its own called DUMPSTER
                    RENTAL, with its own statement and its own balance.

                    The default is left alone: a client's next job really is
                    a new project, and guessing otherwise would silently
                    merge two jobs, which is the worse mistake and the harder
                    one to see. What was missing is that the screen never
                    said the client already had one. Now it does, and names
                    it, so the choice is made with both facts on screen. */}
                {!f.projectId && theirs.length > 0 && (
                  <div className="alert alert-amber inv-second" style={{ margin: '0 0 10px', display: 'block', fontSize: 12.5, lineHeight: 1.5 }}>
                    <strong>{f.client.name} already has {theirs.length === 1 ? 'a project' : `${theirs.length} projects`}.</strong>{' '}
                    This raises a SECOND one. If this bill belongs on {theirs.length === 1 ? theirs[0].title : 'one of them'},
                    pick it from Which project above, or this client ends up with two statements and two balances.
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group"><label>Invoice #</label>
                    <input type="text" value={f.invoiceNumber} onChange={e => setInv({ invoiceNumber: e.target.value })}
                      placeholder="Assigned for you" /></div>
                  <div className="form-group"><label>Date</label>
                    <input type="date" value={f.invoiceDate} onChange={e => setInv({ invoiceDate: e.target.value })} /></div>
                </div>
                {/* Left blank it comes back as INV-<year>-<0000>, counted on
                    the server. Typed over when the invoice already exists in
                    Joist and this is the statement catching up. */}
                <div style={{ fontSize: 12, color: 'var(--muted)', margin: '-4px 0 4px', lineHeight: 1.5 }}>
                  Leave the number blank and one is assigned. Type one to match an invoice you have already raised elsewhere.
                </div>

                {/* ── ONE BILL, OR ONE PER TRADE ───────────────────────────
                    The basement estimate was $42,013.40 across nineteen
                    trades, cut up by hand into nineteen numbered invoices so
                    the homeowner pays for demolition when demolition is done.
                    That is the cash flow the job runs on. This does it.

                    Offered WITH a typed number now. A typed number used to
                    hide this, on the reasoning that naming a number means
                    naming one document — but Julian types 1877 because that
                    is where Joist left off, and still wants nine milestones
                    payable one at a time. The number seeds the run. */}
                {(() => {
                  const n = f.lines.filter(l => (l.label || '').trim() || l.cost).length;
                  if (n <= 1) return null;
                  const start = f.invoiceNumber.trim();
                  return (
                    <label className="inv-split">
                      <input type="checkbox" checked={Boolean(f.split)}
                        onChange={e => setInv({ split: e.target.checked })} />
                      <span>
                        <b>One invoice per line</b>
                        <small>
                          {f.split
                            ? `${n} separate invoices${start ? `, running on from ${start}` : ', numbered in a row'}. They pay each trade off as it finishes.`
                            : start
                              ? `One bill, all of it numbered ${start}. They pay it at once or not at all.`
                              : 'One bill for everything. They pay it all at once or not at all.'}
                        </small>
                      </span>
                    </label>
                  );
                })()}

                {/* ── SEND IT, OR JUST RECORD IT ───────────────────────────
                    OFF by default, which is Julian's call and matches how he
                    works: he raises a bill, looks at it, and sends the
                    customer a pay link himself.

                    The one thing this costs him is said on the label rather
                    than left to be discovered. Nothing in the app emails an
                    invoice after it is raised — this switch is the only
                    send — so a bill he meant to send and left unticked has
                    to go out as a pay link copied off the statement. */}
                <label className="inv-split">
                  <input type="checkbox" checked={f.notify === true}
                    onChange={e => setInv({ notify: e.target.checked })} />
                  <span>
                    <b>Email it to them now</b>
                    <small>
                      {f.notify === true
                        ? 'They get the bill and a pay link straight away.'
                        : 'Nothing is sent. The statement records the work and they hear nothing, which is what you want when you are catching up on work already agreed. This is the only moment the app offers to email it, so tick it if this bill is one they are waiting for.'}
                    </small>
                  </span>
                </label>
              </div>
            }
            lines={f.lines}
            onChangeLines={(lines) => setInv({ lines })}
            adjustments={f.split ? [] : [
              // Not offered when splitting. A discount raised as its own
              // invoice is a bill for -$200 with a Pay button on it, and the
              // server refuses one.
              { key: 'discount', label: 'Discount', value: f.discount, sign: -1, onChange: (v) => setInv({ discount: v }) },
              { key: 'tax', label: 'Tax', value: f.tax, sign: 1, onChange: (v) => setInv({ tax: v }) },
            ]}
            doneLabel={f.split && f.lines.filter(l => (l.label || '').trim() || l.cost).length > 1
              ? `Raise ${f.lines.filter(l => (l.label || '').trim() || l.cost).length} Invoices`
              : 'Raise Invoice'}
            onDone={handleSaveClientInvoice}
            saving={isSavingInvoice}
            backLabel="Invoice"
            onCancel={() => { setModal(null); setClientInvoiceForm(null); }}
          />
        );
      })()}

      {/* Set Appointment Modal (for an existing lead) */}
      <div className={`modal ${modal === 'appointment' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">Schedule Appointment</div>
          {activeRequest && <div className="qps-sheet-sub">{activeRequest.fullName} · {activeRequest.serviceType}</div>}

          <div className="form-group"><label>Date & Time of in-person estimate</label><input type="datetime-local" value={appointmentInput} onChange={e => setAppointmentInput(e.target.value)} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleSetAppointment}>Save Appointment</button>
          {appointmentInput && <button className="btn btn-ghost btn-lg" style={{ marginBottom: 10 }} onClick={() => setAppointmentInput('')}>Clear</button>}
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'appointment' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Add a commercial account ─────────────────────────────────────
          Self-signup at /commercial is for the company that finds us. This is
          for the one Julian is already working with, who is not going to fill
          in a web form because he asked them to. */}
      <div className={`modal ${modal === 'adminAccount' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">Add a Commercial Account</div>
          <div className="qps-sheet-sub">A property manager, HOA, realtor or investor.</div>

          {accountForm && (
            <>
              <div className="form-group"><label>Company name <span className="req">*</span></label>
                <input type="text" value={accountForm.name} onChange={e => setAccountForm(p => ({ ...p, name: e.target.value }))} placeholder="Conrad Cruz Property Management" /></div>
              <div className="form-group"><label>What kind</label>
                <select value={accountForm.type} onChange={e => setAccountForm(p => ({ ...p, type: e.target.value }))}>
                  <option value="property-management">Property management</option>
                  <option value="hoa">HOA</option>
                  <option value="realtor">Realtor</option>
                  <option value="investor">Investor</option>
                  <option value="other">Other</option>
                </select></div>
              <div className="form-row">
                <div className="form-group"><label>Office phone</label>
                  <input type="tel" value={accountForm.phone} onChange={e => setAccountForm(p => ({ ...p, phone: e.target.value }))} /></div>
                <div className="form-group"><label>Office email</label>
                  <input type="email" value={accountForm.email} onChange={e => setAccountForm(p => ({ ...p, email: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Billing address</label>
                <input type="text" value={accountForm.billingAddress} onChange={e => setAccountForm(p => ({ ...p, billingAddress: e.target.value }))} /></div>
              <div className="form-group">
                <label>Spending limit (leave blank for none)</label>
                <input type="number" inputMode="decimal" min="0" value={accountForm.defaultNteAmount}
                  onChange={e => setAccountForm(p => ({ ...p, defaultNteAmount: e.target.value }))} placeholder="e.g. 300" />
                {/* Worth saying plainly: blank is not "no rules", it is "ask me
                    every time", which feels broken from their side. */}
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Pre-fills every work order they raise. Left blank, <strong>every estimate comes back to them
                  to approve</strong> — agree a number with them first.
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', margin: '18px 0 14px', paddingTop: 16 }}>
                <div className="sec-label">Their first login (optional)</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
                  Whoever you name here becomes the <strong>owner</strong> of the account and can add the rest of
                  their team themselves. You can leave it and add them later.
                </div>
                <div className="form-group"><label>Their name</label>
                  <input type="text" value={accountForm.ownerName} onChange={e => setAccountForm(p => ({ ...p, ownerName: e.target.value }))} placeholder="Courtney Diment" /></div>
                <div className="form-row">
                  <div className="form-group"><label>Their email</label>
                    <input type="email" value={accountForm.ownerEmail} onChange={e => setAccountForm(p => ({ ...p, ownerEmail: e.target.value }))} /></div>
                  <div className="form-group"><label>Their phone</label>
                    <input type="tel" value={accountForm.ownerPhone} onChange={e => setAccountForm(p => ({ ...p, ownerPhone: e.target.value }))} /></div>
                </div>
                <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5 }}>
                  <input type="checkbox" checked={accountForm.sendInvite !== false}
                    onChange={e => setAccountForm(p => ({ ...p, sendInvite: e.target.checked }))} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 13 }}>Email them a link to set their own password. Untick if you would
                    rather read them the link yourself — either way no password is ever sent by email.</span>
                </label>
              </div>

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleCreateAccount}>Create Account</button>
            </>
          )}

          {/* Shown after a successful invite. Reading this out is more reliable
              than an email landing in a spam folder nobody checks. */}
          {lastInviteLink && (
            <div className="alert alert-green" style={{ marginBottom: 12, wordBreak: 'break-all', fontSize: 12.5, flexDirection: 'column' }}>
              <strong style={{ display: 'block', marginBottom: 6 }}>Their set-password link — good for 7 days</strong>
              {lastInviteLink}
              <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }}
                onClick={() => { navigator.clipboard?.writeText(lastInviteLink); showToast('Link copied'); }}>Copy link</button>
            </div>
          )}

          <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setAccountForm(null); setLastInviteLink(''); }}>
            {lastInviteLink ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
      {modal === 'adminAccount' && <div className="overlay active" onClick={() => { setModal(null); setLastInviteLink(''); }}></div>}

      {/* ── Add somebody to a commercial account ────────────────────────── */}
      <div className={`modal ${modal === 'adminAccountMember' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">
            {accountMemberForm?.userId ? 'Access Level' : 'Add Someone'}
          </div>
          {accountMemberForm && (
            <div className="qps-sheet-sub">{accountMemberForm.accountName}</div>
          )}

          {accountMemberForm && (
            <>
              {!accountMemberForm.userId ? (
                <>
                  <div className="form-group"><label>Their name <span className="req">*</span></label>
                    <input type="text" value={accountMemberForm.name} onChange={e => setAccountMemberForm(p => ({ ...p, name: e.target.value }))} /></div>
                  <div className="form-group"><label>Their email <span className="req">*</span></label>
                    <input type="email" value={accountMemberForm.email} onChange={e => setAccountMemberForm(p => ({ ...p, email: e.target.value }))} /></div>
                  <div className="form-group"><label>Their phone</label>
                    <input type="tel" value={accountMemberForm.phone} onChange={e => setAccountMemberForm(p => ({ ...p, phone: e.target.value }))} /></div>
                </>
              ) : (
                <div style={{ fontSize: 14, marginBottom: 16 }}>{accountMemberForm.name} · {accountMemberForm.email}</div>
              )}

              {/* Radio rows, not a dropdown: the difference between these three
                  IS the point, and a select hides two thirds of it behind a tap. */}
              <div className="form-group">
                <label>What they can do</label>
                {[
                  ['owner', 'Owner', 'Everything, plus adding people and changing company settings.'],
                  ['manager', 'Manager', 'Raise work orders and approve estimates that go over the limit.'],
                  ['coordinator', 'Coordinator', 'Raise work orders and follow them. Cannot approve spending over the limit.'],
                ].map(([value, label, desc]) => (
                  <label key={value} className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 10 }}>
                    <input type="radio" name="adminMemberLevel" checked={accountMemberForm.level === value}
                      onChange={() => setAccountMemberForm(p => ({ ...p, level: value }))} style={{ marginTop: 3 }} />
                    <span>
                      <span style={{ display: 'block', fontSize: 14, color: 'var(--navy)' }}>{label}</span>
                      <span style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{desc}</span>
                    </span>
                  </label>
                ))}
              </div>

              {!accountMemberForm.userId && (
                <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 14 }}>
                  <input type="checkbox" checked={accountMemberForm.sendInvite !== false}
                    onChange={e => setAccountMemberForm(p => ({ ...p, sendInvite: e.target.checked }))} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 13 }}>Email them a link to set their own password.</span>
                </label>
              )}

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleSaveAccountMember}>
                {accountMemberForm.userId ? 'Save' : 'Add Them'}
              </button>
            </>
          )}

          {lastInviteLink && (
            <div className="alert alert-green" style={{ marginBottom: 12, wordBreak: 'break-all', fontSize: 12.5, flexDirection: 'column' }}>
              <strong style={{ display: 'block', marginBottom: 6 }}>Their set-password link — good for 7 days</strong>
              {lastInviteLink}
              <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }}
                onClick={() => { navigator.clipboard?.writeText(lastInviteLink); showToast('Link copied'); }}>Copy link</button>
            </div>
          )}

          <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setAccountMemberForm(null); setLastInviteLink(''); }}>
            {lastInviteLink ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
      {modal === 'adminAccountMember' && <div className="overlay active" onClick={() => { setModal(null); setLastInviteLink(''); }}></div>}

      {/* ── Add a property to a commercial account ───────────────────────
          Their own people can add properties from their portal. This is the
          same act on the day the account is brought on board, when nobody
          there has signed in yet and Julian has the buildings in front of him.

          Only the address is required. The gate and lockbox codes are here
          because they are what a tech is actually stuck without, and asking
          for them while the manager is still on the phone is the only time
          anyone reliably answers. Everything else the property record holds —
          the furnace, the owner, the units — they fill in from their side. */}
      <div className={`modal ${modal === 'adminProperty' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">Add a Property</div>
          {adminPropertyForm && (
            <div className="qps-sheet-sub">{adminPropertyForm.accountName}</div>
          )}

          {adminPropertyForm && (
            <>
              <div className="form-group"><label>Street address <span className="req">*</span></label>
                <input type="text" value={adminPropertyForm.addressLine1}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, addressLine1: e.target.value }))} placeholder="400 W Main St" /></div>
              <div className="form-group"><label>Unit / suite</label>
                <input type="text" value={adminPropertyForm.addressLine2}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, addressLine2: e.target.value }))} placeholder="Apt 3" /></div>
              {/* Three across rather than the usual two: the state is two
                  characters and does not deserve a line of its own, and Julian
                  takes properties in Idaho and Wyoming as well as Utah. */}
              <div className="form-row" style={{ gridTemplateColumns: '2fr 1fr 1.2fr' }}>
                <div className="form-group"><label>City</label>
                  <input type="text" value={adminPropertyForm.city}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, city: e.target.value }))} /></div>
                <div className="form-group"><label>State</label>
                  <input type="text" maxLength={2} autoCapitalize="characters" value={adminPropertyForm.state}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, state: e.target.value.toUpperCase() }))} /></div>
                <div className="form-group"><label>ZIP</label>
                  <input type="text" inputMode="numeric" value={adminPropertyForm.zip}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, zip: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>What kind</label>
                <select value={adminPropertyForm.propertyType}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, propertyType: e.target.value }))}>
                  <option value="single-family">Single family</option>
                  <option value="condo">Condo</option>
                  <option value="townhome">Townhome</option>
                  <option value="duplex">Duplex</option>
                  <option value="multiplex">Multiplex</option>
                  <option value="apartment">Apartment</option>
                  <option value="commercial">Commercial</option>
                  <option value="other">Other</option>
                </select></div>
              <div className="form-group"><label>What they call it</label>
                <input type="text" value={adminPropertyForm.label}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, label: e.target.value }))} placeholder="Cedar Park 4-plex" /></div>
              <div className="form-group">
                <label>Their code for it</label>
                <input type="text" value={adminPropertyForm.externalId}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, externalId: e.target.value }))} placeholder="P-100" />
                {/* Their own id out of AppFolio or Buildium. It is what makes our
                    records match their books, and what a later spreadsheet
                    import matches on instead of creating a second copy. */}
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Their number for this property in AppFolio, Buildium or Yardi — it keeps our records lined up with theirs.
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Gate code</label>
                  <input type="text" value={adminPropertyForm.access?.gateCode || ''}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, access: { ...(p.access || {}), gateCode: e.target.value } }))} /></div>
                <div className="form-group"><label>Lockbox code</label>
                  <input type="text" value={adminPropertyForm.access?.lockboxCode || ''}
                    onChange={e => setAdminPropertyForm(p => ({ ...p, access: { ...(p.access || {}), lockboxCode: e.target.value } }))} /></div>
              </div>
              <div className="form-group">
                <label>Spending limit for this property</label>
                <input type="number" inputMode="decimal" min="0" value={adminPropertyForm.nteAmount}
                  onChange={e => setAdminPropertyForm(p => ({ ...p, nteAmount: e.target.value }))} placeholder="Uses the company limit" />
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Leave blank and this property uses the company-wide limit. Set one only where this building needs to be different.
                </div>
              </div>

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleCreateAdminProperty}>Add Property</button>
            </>
          )}

          <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setAdminPropertyForm(null); }}>Cancel</button>
        </div>
      </div>
      {modal === 'adminProperty' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Start a job at a property, to price it or to bill it ─────────
          Short on purpose. The address, the unit's tenant, the access codes and
          the spending limit are all on the property already — the only thing
          nobody can know is what the work is, and Julian is usually standing in
          front of it. Saving opens the estimate or the invoice sheet next. */}
      <div className={`modal ${modal === 'adminPropertyJob' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">
            {propertyJobForm?.next === 'invoice' ? 'New Invoice' : 'New Estimate'}
          </div>
          {propertyJobForm && (
            <div className="qps-sheet-sub">{propertyJobForm.propertyLabel}</div>
          )}

          {propertyJobForm && (
            <>
              {/* A property with units needs one named. Billing "400 W Main"
                  when the work was in #103 is how the wrong owner gets charged. */}
              {propertyJobForm.units?.length > 0 && (
                <div className="form-group">
                  <label>Which unit</label>
                  <select value={propertyJobForm.unitId} onChange={e => setPropertyJobForm(p => ({ ...p, unitId: e.target.value }))}>
                    <option value="">The whole building</option>
                    {propertyJobForm.units.map(u => (
                      <option key={u._id} value={u._id}>#{u.unitNumber}{u.tenantName ? ` · ${u.tenantName}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Kind of work</label>
                <select value={propertyJobForm.serviceType} onChange={e => setPropertyJobForm(p => ({ ...p, serviceType: e.target.value }))}>
                  {SERVICE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>What is the work <span className="req">*</span></label>
                <textarea rows="3" value={propertyJobForm.description}
                  onChange={e => setPropertyJobForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Full turn — paint, carpet clean, replace the disposal" />
              </div>

              <div className="form-group">
                <label>How urgent</label>
                <select value={propertyJobForm.priority} onChange={e => setPropertyJobForm(p => ({ ...p, priority: e.target.value }))}>
                  <option value="routine">Routine</option>
                  <option value="urgent">Urgent — 48 hours</option>
                  <option value="emergency">Emergency — same day</option>
                </select>
              </div>

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleCreatePropertyJob}>
                {propertyJobForm.next === 'invoice' ? 'Next — write the invoice' : 'Next — write the estimate'}
              </button>
            </>
          )}

          <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setPropertyJobForm(null); }}>Cancel</button>
        </div>
      </div>
      {modal === 'adminPropertyJob' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Change Password. Reached from the admin header AND from the account
          group in a technician's More menu and a client's. It used to be
          admin-only and written in English, which is why it is translated
          now: a technician who works in Spanish has to be able to read the
          screen where they change their own password. */}
      <div className={`modal ${modal === 'password' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">{t('pw.title')}</div>

          <div className="form-group"><label>{t('pw.current')}</label><input type="password" autoComplete="current-password" value={passwordForm.current} onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))} /></div>
          <div className="form-group"><label>{t('pw.new')}</label><input type="password" autoComplete="new-password" value={passwordForm.next} onChange={e => setPasswordForm(p => ({ ...p, next: e.target.value }))} /></div>
          <div className="form-group"><label>{t('pw.confirm')}</label><input type="password" autoComplete="new-password" value={passwordForm.confirm} onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleChangePassword}>{t('pw.save')}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'password' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* Change the address you sign in with (admin nav → Email) */}
      <div className={`modal ${modal === 'email' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">{t('em.title')}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, lineHeight: 1.5 }}>
            {t('em.sub')}
          </div>

          <div className="form-group"><label>{t('pw.current')}</label><input type="password" autoComplete="current-password" value={emailForm.current} onChange={e => setEmailForm(p => ({ ...p, current: e.target.value }))} /></div>
          <div className="form-group"><label>{t('em.new')}</label><input type="email" autoComplete="email" placeholder="you@example.com" value={emailForm.next} onChange={e => setEmailForm(p => ({ ...p, next: e.target.value }))} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} onClick={handleChangeEmail}>{t('em.save')}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'email' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Who else can run this place ───────────────────────────────────
          There was no way to add a second admin, which meant one forgotten
          password with email switched off locked the company out of its own
          back office. The set-password link comes back to this screen rather
          than only to an inbox, because the inbox may not work. */}
      <div className={`modal ${modal === 'admins' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">Admins</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, lineHeight: 1.5 }}>
            Anyone here can see every lead, every technician and every invoice,
            and can approve invoices. Add somebody only if that is true of them.
            {' '}Refunds are separate: money leaving the company is off until you
            switch it on for a person by name.
          </div>

          {admins.loading ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '6px 0 14px' }}>Loading…</div>
          ) : admins.error ? (
            <div className="alert alert-red" style={{ margin: '0 0 14px' }}>{admins.error}</div>
          ) : (
            <div className="tp-rows" style={{ marginBottom: 18, maxWidth: 'none' }}>
              {admins.list.map(a => (
                <div key={a._id} className="tp-row" style={{ cursor: 'default' }}>
                  <div className="tp-row-main">
                    <div className="tp-row-name">{a.name}</div>
                    <div className="tp-row-meta">
                      {a.email}
                      {a.inviteOutstanding ? ' · has not set a password yet' : ''}
                    </div>
                  </div>
                  <div className="tp-row-right" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Refunds, handed out one person at a time. The owner is
                        the first admin account ever created: they hold it
                        always, because an app where nobody can refund and
                        nobody can grant the right to is an app only a database
                        console gets you back into. */}
                    {a.isOwner ? (
                      <span className="tag tag-green" title="The first admin account. Holds every permission and cannot have one removed.">
                        Owner · can refund
                      </span>
                    ) : (
                      <label className="ap-perm" title="Let this person issue refunds">
                        <span>Refunds</span>
                        <span className={`dopt-switch${(a.permissions || []).includes('refunds') ? ' on' : ''}`}>
                          <input
                            type="checkbox"
                            checked={(a.permissions || []).includes('refunds')}
                            onChange={e => toggleAdminPermission(a, 'refunds', e.target.checked)}
                            style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
                          />
                          <span className="dopt-knob"></span>
                        </span>
                      </label>
                    )}
                    <button type="button" className="btn btn-outline btn-sm"
                      onClick={() => handleSendSignInLink(a._id, a.name)}>
                      Sign-in link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="sec-label">Add an admin</div>
          <div className="form-group"><label>Name</label><input type="text" value={adminForm.name} onChange={e => setAdminForm(p => ({ ...p, name: e.target.value }))} placeholder="The person" /></div>
          <div className="form-group">
            <label>Email Address <span className="req">*</span></label>
            <input type="email" value={adminForm.email} onChange={e => setAdminForm(p => ({ ...p, email: e.target.value }))} placeholder="them@qpshub.com" />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              This is what they sign in with. An admin has nothing else to sign in with, so it is required.
            </div>
          </div>
          <div className="form-group"><label>Phone Number</label><input type="tel" value={adminForm.phone} onChange={e => setAdminForm(p => ({ ...p, phone: e.target.value }))} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingAdmin} onClick={handleAddAdmin}>
            {isSavingAdmin ? 'Adding…' : 'Add Admin'}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Close</button>
        </div>
      </div>
      {modal === 'admins' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* The link itself, kept on screen until it is dismissed. Above
          everything, because it is worthless the moment it scrolls away. */}
      {signInLink && (
        <>
          <div className="confirm-overlay" onClick={() => setSignInLink(null)}></div>
          <div className="confirm-box" style={{ maxWidth: 460 }}>
            <div className="confirm-title">
              {signInLink.who ? `Link for ${signInLink.who}` : 'Their sign-in link'}
            </div>
            <div className="confirm-text" style={{ marginBottom: 12 }}>{signInLink.message}</div>
            <input
              readOnly
              className="stc-link"
              value={signInLink.link}
              onFocus={e => e.target.select()}
            />
            <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }}
              onClick={() => {
                const box = document.querySelector('.confirm-box .stc-link');
                navigator.clipboard?.writeText(signInLink.link).catch(() => {});
                box?.select();
                showToast('Link copied');
              }}>
              Copy link
            </button>
            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.55, marginBottom: 12 }}>
              Good for 7 days, and it sets their password once. Text it or read
              it out. Anyone holding it can get into that account, so do not
              post it anywhere.
            </div>
            <button className="btn btn-ghost btn-lg" onClick={() => setSignInLink(null)}>Done</button>
          </div>
        </>
      )}

      {/* Confirm Deletion dialog (admin) — centered, Joist-style */}
      {confirmDelete && (
        <>
          <div className="confirm-overlay" onClick={() => setConfirmDelete(null)}></div>
          <div className="confirm-box">
            <div className="confirm-title">Confirm Deletion</div>
            <div className="confirm-text">
              Are you sure you want to delete this {confirmDelete.type}?
              {confirmDelete.label && <><br /><strong>{confirmDelete.label}</strong></>}
              <br /><span style={{ fontSize: 12, color: 'var(--muted)' }}>This can't be undone.</span>
            </div>
            <div className="confirm-actions">
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-red" onClick={handleConfirmDelete}>Delete</button>
            </div>
          </div>
        </>
      )}

      {/* ── Ledger: new / edit invoice ─────────────────────────── */}
      <div className={`modal ${modal === 'ledgerEntry' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">
            {ledgerForm._id ? 'Edit Ledger Entry' : isAdmin ? 'New Ledger Entry' : t('lg.submitInvoice')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
            {isAdmin ? 'Log a vendor invoice so you can track payments against it.' : t('lg.noInvoices')}
          </div>

          {isAdmin && !ledgerForm._id && (
            <div className="form-group">
              <label>Vendor <span className="req">*</span></label>
              <select value={ledgerForm.vendorId} onChange={e => setLedger({ vendorId: e.target.value })}>
                <option value="">Choose a vendor…</option>
                {vendors.map(v => (
                  <option key={v._id} value={v._id}>
                    {v.businessName}{v.compliance && !v.compliance.canBePaid ? ' — paperwork expired' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!ledgerForm._id && (
            <div className="form-group">
              <label>{isAdmin ? 'Link to a job (optional)' : t('lg.tabJobs')}</label>
              <select value={ledgerForm.requestId} onChange={e => {
                const job = requests.find(r => r._id === e.target.value);
                setLedger({
                  requestId: e.target.value,
                  serviceAddress: job?.serviceAddress || ledgerForm.serviceAddress,
                  jobDetails: job ? `${job.serviceType} — ${job.description || ''}`.trim() : ledgerForm.jobDetails
                });
              }}>
                <option value="">—</option>
                {requests.map(r => (
                  <option key={r._id} value={r._id}>{optLabel(r.serviceType)} · {r.serviceAddress}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Invoice #</label>
              <input type="text" value={ledgerForm.invoiceNumber} onChange={e => setLedger({ invoiceNumber: e.target.value })} placeholder="Auto" />
            </div>
            <div className="form-group">
              <label>{t('lg.invoice')} <span className="req">*</span></label>
              <input type="number" step="0.01" min="0" value={ledgerForm.invoiceAmount} onChange={e => setLedger({ invoiceAmount: e.target.value })} placeholder="$0.00" />
            </div>
          </div>

          <div className="form-group"><label>{t('req.serviceAddress')}</label><input type="text" value={ledgerForm.serviceAddress} onChange={e => setLedger({ serviceAddress: e.target.value })} /></div>
          <div className="form-group"><label>{t('req.jobDetails')}</label><textarea value={ledgerForm.jobDetails} onChange={e => setLedger({ jobDetails: e.target.value })} /></div>

          {/* Wherever this invoice already lives. Same field as the estimate,
              for the same reason: a tech who raised it in Joist should link it
              rather than retype it. */}
          <div className="form-group">
            <label htmlFor="lg-url">Link to your invoice <span className="dx-opt">(optional)</span></label>
            <input id="lg-url" type="url" inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck="false"
              value={ledgerForm.externalUrl || ''} onChange={e => setLedger({ externalUrl: e.target.value })}
              placeholder="joist.com/…  ·  where your invoice already lives" />
          </div>

          {!ledgerForm._id && (
            <div className="form-group">
              <label>{t('lg.invoiceFile')}</label>
              <input type="file" accept="image/*,.pdf" multiple onChange={e => {
                // Read the FileList NOW, before the input is cleared. Passing
                // it into the updater meant React called that closure later,
                // by which time `value = ''` had already emptied it — so every
                // pick added nothing and the picker looked broken.
                const picked = Array.from(e.target.files);
                e.target.value = '';
                setLedgerDocFiles(prev => [...prev, ...picked].slice(0, 5));
              }} />
              {ledgerDocFiles.length > 0 && (
                <div className="file-chips">
                  {ledgerDocFiles.map((file, i) => (
                    <span key={i} className="file-chip">{file.name}
                      <span style={{ marginLeft: 6, cursor: 'pointer' }} onClick={() => setLedgerDocFiles(prev => prev.filter((_, idx) => idx !== i))}>×</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {isAdmin && ledgerForm._id && (
            <div className="form-group">
              <label>Status</label>
              <select value={ledgerForm.status} onChange={e => setLedger({ status: e.target.value })}>
                {LEDGER_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
          )}

          <div className="form-group"><label>Notes</label><textarea value={ledgerForm.notes} onChange={e => setLedger({ notes: e.target.value })} /></div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingLedger} onClick={handleSaveLedgerEntry}>
            {isSavingLedger ? t('req.submitting') : ledgerForm._id ? 'Save Changes' : isAdmin ? 'Add to Ledger' : t('lg.submitInvoice')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'ledgerEntry' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Ledger: record a payment (admin only, English) ─────── */}
      <div className={`modal ${modal === 'payment' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">Record Payment</div>
          {activeEntry && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              {activeEntry.vendorId?.businessName} · {activeEntry.invoiceNumber} · balance {fmtMoney(balanceOf(activeEntry))}
            </div>
          )}

          {/* Server refused: this vendor's insurance or licenses have lapsed. */}
          {paymentBlock && (
            <div className="alert alert-red">
              <div>
                <strong>{paymentBlock.message}</strong>
                <div style={{ marginTop: 4 }}>{(paymentBlock.blockers || []).join(' · ')}</div>
                <div style={{ marginTop: 6 }}>
                  Paying a vendor without current insurance or a valid license puts QPS on the hook legally. Get the
                  renewed document uploaded first — or, if you're paying anyway, write down why below.
                </div>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group"><label>Amount <span className="req">*</span></label><input type="number" step="0.01" min="0" value={paymentForm.amount} onChange={e => setPay({ amount: e.target.value })} placeholder="$0.00" /></div>
            <div className="form-group"><label>Date</label><input type="date" value={paymentForm.date} onChange={e => setPay({ date: e.target.value })} /></div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Mode of Payment</label>
              <select value={paymentForm.mode} onChange={e => setPay({ mode: e.target.value })}>
                {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Reference</label><input type="text" value={paymentForm.reference} onChange={e => setPay({ reference: e.target.value })} placeholder="Check # / confirmation #" /></div>
          </div>

          <div className="form-group"><label>Note</label><input type="text" value={paymentForm.note} onChange={e => setPay({ note: e.target.value })} placeholder="Optional" /></div>

          {paymentBlock && (
            <div className="form-group">
              <label style={{ color: 'var(--red)' }}>Override reason <span className="req">*</span></label>
              <textarea value={paymentForm.overrideReason} onChange={e => setPay({ overrideReason: e.target.value })} placeholder="e.g. Work predates the lapse — renewal certificate received by text, uploading tomorrow" />
            </div>
          )}

          <button
            className={`btn btn-lg ${paymentBlock ? 'btn-red' : 'btn-green'}`}
            style={{ marginBottom: 10 }}
            disabled={paymentBlock && !paymentForm.overrideReason}
            onClick={handleRecordPayment}
          >
            {paymentBlock ? 'Pay Anyway (Override)' : 'Record Payment'}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'payment' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Ledger: live progress report ───────────────────────── */}
      <div className={`modal ${modal === 'progress' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">{t('lg.addProgress')}</div>
          {activeEntry && <div className="qps-sheet-sub">{activeEntry.invoiceNumber}{activeEntry.serviceAddress ? ` · ${activeEntry.serviceAddress}` : ''}</div>}

          <div className="form-group">
            <label>{t('req.photosVideos')}</label>
            {/* A label wrapping the input, not a div calling .click() on a
                hidden one. The indirection is what every other picker in this
                app already dropped: a real label opens the sheet through the
                browser's own gesture handling, with nothing between the tap
                and the file. */}
            <label className="upload-zone" style={{ cursor: 'pointer', display: 'block' }}>
              <input id="progress-input" type="file" accept="image/*,video/*" multiple hidden onChange={e => {
                // Read the FileList NOW, before the input is cleared. Passing
                // it into the updater meant React called that closure later,
                // by which time `value = ''` had already emptied it — so every
                // pick added nothing and the picker looked broken.
                const picked = Array.from(e.target.files);
                e.target.value = '';
                setProgressFiles(prev => [...prev, ...picked].slice(0, 10));
              }} />
              {progressFiles.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                  {progressFiles.map((f, i) => (
                    <span key={i} className="file-chip">{f.name}
                      <span style={{ marginLeft: 6, cursor: 'pointer' }}
                        onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setProgressFiles(prev => prev.filter((_, idx) => idx !== i)); }}>×</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>{t('req.tapToUpload')}</p>
              )}
            </label>
          </div>

          <div className="form-group">
            <label>{t('lg.jobProgress')} %</label>
            <input type="number" min="0" max="100" value={progressForm.percentComplete} onChange={e => setProgressForm(p => ({ ...p, percentComplete: e.target.value }))} placeholder="60" />
          </div>

          <div className="form-group"><label>{t('req.jobDetails')}</label><textarea value={progressForm.note} onChange={e => setProgressForm(p => ({ ...p, note: e.target.value }))} /></div>

          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} disabled={isSavingLedger} onClick={handleAddProgress}>
            {isSavingLedger ? t('req.submitting') : t('lg.addProgress')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'progress' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Compliance document (insurance / license) ──────────── */}
      <div className={`modal ${modal === 'credential' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">{t('lg.uploadRenewal')}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>{t('lg.docsIntro')}</div>

          <div className="form-group">
            <label>{t('lg.tabDocs')} <span className="req">*</span></label>
            <select value={credentialForm.type} onChange={e => setCred({ type: e.target.value })}>
              {CREDENTIAL_TYPES.map(ct => <option key={ct} value={ct}>{credentialLabel(ct)}</option>)}
            </select>
          </div>

          {credentialForm.type === 'other' && (
            <div className="form-group"><label>{t('lg.type.other')} <span className="req">*</span></label><input type="text" value={credentialForm.label} onChange={e => setCred({ label: e.target.value })} /></div>
          )}

          <div className="form-row">
            <div className="form-group"><label>Issuer</label><input type="text" value={credentialForm.issuer} onChange={e => setCred({ issuer: e.target.value })} /></div>
            <div className="form-group"><label>{neverExpires(credentialForm.type) ? 'EIN' : '#'}</label><input type="text" value={credentialForm.number} onChange={e => setCred({ number: e.target.value })} /></div>
          </div>

          {/* A W-9 has no expiry date. Showing the field anyway invites a made-up
              one, and a made-up date fires a renewal reminder for a form
              nobody has to renew. */}
          {neverExpires(credentialForm.type) ? (
            <div className="help-line">{t('lg.noExpiry', 'No expiry — signed once')}</div>
          ) : (
            <div className="form-group"><label>{t('lg.expiresOn')} <span className="req">*</span></label><input type="date" value={credentialForm.expiresAt} onChange={e => setCred({ expiresAt: e.target.value })} /></div>
          )}

          <div className="form-group">
            <label>{t('lg.upload')}</label>
            <input type="file" accept="image/*,.pdf" onChange={e => setCredentialFile(e.target.files[0] || null)} />
            {credentialFile && <div className="file-chips"><span className="file-chip">{credentialFile.name}</span></div>}
          </div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingLedger} onClick={handleSaveCredential}>
            {isSavingLedger ? t('req.submitting') : t('lg.upload')}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'credential' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Job conversation (homeowner / technician / QPS) ────── */}
      <div className={`modal ${modal === 'messages' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">{t('msg.title')}</div>
          {modal === 'messages' && <MessageThread />}
          <button className="btn btn-ghost btn-lg" style={{ marginTop: 10 }} onClick={() => setModal(null)}>{t('req.cancel')}</button>
        </div>
      </div>
      {modal === 'messages' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Project (admin) ────────────────────────────────────── */}
      <div className={`modal ${modal === 'project' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">
            {projectForm._id ? 'Edit Project' : 'New Project'}
          </div>
          <div className="form-group"><label>Project name <span className="req">*</span></label><input type="text" value={projectForm.title} onChange={e => setProj({ title: e.target.value })} placeholder="Basement Finish" /></div>
          <div className="form-group">
            <label>Client</label>
            <select style={sel} value={projectForm.homeownerId} onChange={e => setProj({ homeownerId: e.target.value })}>
              <option value="">Not linked to an account</option>
              {/* A client added by hand may have a phone and no email, so the
                  second half of the label is whichever one they gave. */}
              {(clients || []).map(c => (
                <option key={c._id} value={c._id}>{[c.name, c.email || c.phone].filter(Boolean).join(' — ')}</option>
              ))}
            </select>
          </div>
          {!projectForm.homeownerId && (
            <div className="form-group"><label>Client name</label><input type="text" value={projectForm.clientName} onChange={e => setProj({ clientName: e.target.value })} /></div>
          )}
          <div className="form-group"><label>Address</label><input type="text" value={projectForm.address} onChange={e => setProj({ address: e.target.value })} /></div>
          {/* The server has always taken a status; there was no way to set one
              from here, so every project stayed Active forever. Only offered on
              a project that exists — a new one starts active by definition. */}
          {projectForm._id && (
            <div className="form-group"><label>Status</label>
              <select style={sel} value={projectForm.status || 'active'} onChange={e => setProj({ status: e.target.value })}>
                <option value="active">Active — work is running</option>
                <option value="completed">Completed — work is done</option>
                <option value="closed">Closed — done and settled up</option>
              </select>
            </div>
          )}
          <div className="form-group"><label>Notes</label><textarea value={projectForm.notes} onChange={e => setProj({ notes: e.target.value })} /></div>
          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSaveProject}>{isSavingProject ? 'Saving…' : 'Save Project'}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'project' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Edit a technician's details (admin) ────────────────────
          Techs fill the application in on a phone and it shows: a misspelled
          name, a digit missing from a number, "Por trabajo" typed into a rate.
          This lets Julian fix it without asking them to redo the form.

          Trades aren't here on purpose — those are the board columns, and Move
          card owns them. Neither are insurance and licence documents, which
          carry expiry dates and gate payments (Add / Renew a Document). */}
      {vendorEditForm && (
        <>
          <div className="modal active">
            <div className="modal-box">
              <div className="modal-handle"></div>
              <div className="qps-sheet-title">
                {vendorEditForm._id ? 'Edit Technician' : 'Add a Technician'}
              </div>
              <div className="qps-sheet-sub">
                {vendorEditForm._id
                  ? 'Fix what the application got wrong, without asking them to redo it.'
                  : 'A tech you already work with, who never filled the application in.'}
              </div>

              <div className="form-group"><label>Name</label><input type="text" value={vendorEditForm.contactName} onChange={e => setVendorEdit({ contactName: e.target.value })} placeholder="The person" /></div>
              <div className="form-group"><label>Business Name <span className="req">*</span></label><input type="text" value={vendorEditForm.businessName} onChange={e => setVendorEdit({ businessName: e.target.value })} /></div>
              <div className="form-group"><label>Occupation</label><input type="text" value={vendorEditForm.occupation} onChange={e => setVendorEdit({ occupation: e.target.value })} placeholder="Plumber" /></div>
              <div className="form-group"><label>Phone Number</label><input type="tel" value={vendorEditForm.phone} onChange={e => setVendorEdit({ phone: e.target.value })} /></div>
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" value={vendorEditForm.email} onChange={e => setVendorEdit({ email: e.target.value })} />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  {vendorEditForm._id
                    ? 'This is the contact address on their card. It does not change the email they sign in with.'
                    : 'This becomes the email they sign in with, and where their link to set a password goes. A phone number on its own is fine — they just have no portal until an email is on file.'}
                </div>
              </div>
              {/* The business address in four boxes.
                  One free-text line looked tidier and stored a street with no
                  town on it, which is what then printed on the technician's
                  letterhead and on every estimate their customer reads. The
                  four parts are the shape the record already had — the admin
                  form was the only place still asking for one line. */}
              <div className="form-group"><label>Street Address</label><input type="text" value={vendorEditForm.address} onChange={e => setVendorEdit({ address: e.target.value })} placeholder="482 W Center St" /></div>
              <div className="form-group"><label>City</label><input type="text" value={vendorEditForm.city} onChange={e => setVendorEdit({ city: e.target.value })} placeholder="Provo" /></div>
              <div className="form-row">
                <div className="form-group"><label>State</label><input type="text" maxLength={2} value={vendorEditForm.state} onChange={e => setVendorEdit({ state: e.target.value.toUpperCase() })} placeholder="UT" /></div>
                <div className="form-group"><label>ZIP Code</label><input type="text" inputMode="numeric" maxLength={10} value={vendorEditForm.zip} onChange={e => setVendorEdit({ zip: e.target.value })} placeholder="84601" /></div>
              </div>
              <div className="form-group"><label>Home Address if any</label><input type="text" value={vendorEditForm.homeAddress} onChange={e => setVendorEdit({ homeAddress: e.target.value })} /></div>
              <div className="form-group"><label>Years of experience</label><input type="text" value={vendorEditForm.yearsExperience} onChange={e => setVendorEdit({ yearsExperience: e.target.value })} placeholder="15" /></div>

              {[
                ['hasTools', 'Has their own tools'],
                ['licensed', 'Licensed'],
                ['insured', 'Insured'],
                ['hasCrew', 'Has a crew'],
              ].map(([field, label]) => (
                <div className="form-group" key={field}>
                  <label>{label}</label>
                  <select style={sel} value={vendorEditForm[field]} onChange={e => setVendorEdit({ [field]: e.target.value })}>
                    <option value="">Not answered</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              ))}

              {/* Hourly rate and rate by square foot used to sit here. They
                  were removed: every technician on QPS Hub prices their own
                  work line by line on an estimate, so a rate typed once when
                  they were added was a number nobody priced anything from and
                  that nobody kept up to date. */}

              {/* Trades, on an edit as well as an add. This used to be
                  add-only, on the reasoning that Move card owned the trades
                  once a tech was on the board. It did not survive contact
                  with a real application: Saul ticked ten trades on his
                  phone, the board drew him ten times, and Julian had no way
                  to cut it back to the handyman he actually is. */}
              <div className="sec-label">What do they do?</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {TAXONOMY.map(x => {
                  const on = (vendorEditForm.expertise || []).includes(x.cat);
                  return (
                    <button
                      type="button"
                      key={x.cat}
                      className={`btn btn-sm ${on ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => {
                        const next = on
                          ? (vendorEditForm.expertise || []).filter(c => c !== x.cat)
                          : [...(vendorEditForm.expertise || []), x.cat];
                        // Untick the main trade and the card has nowhere to
                        // sit, so it moves to whatever is left.
                        const main = next.includes(vendorEditForm.primaryTrade)
                          ? vendorEditForm.primaryTrade
                          : (next[0] || '');
                        setVendorEdit({ expertise: next, primaryTrade: main });
                      }}
                    >
                      {x.cat}
                    </button>
                  );
                })}
              </div>

              {/* One card, one column. Everything above is what he can do;
                  this is where you find him. */}
              {(vendorEditForm.expertise || []).length > 1 && (
                <div className="form-group">
                  <label>Main trade — the column their card sits in</label>
                  <select
                    style={sel}
                    value={vendorEditForm.primaryTrade || ''}
                    onChange={e => setVendorEdit({ primaryTrade: e.target.value })}
                  >
                    {(vendorEditForm.expertise || []).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 }}>
                    One card, in this column. Every other trade above still
                    shows on the card, and their name appears under those
                    columns too, so you can still find them by the work.
                  </div>
                </div>
              )}

              <div className="form-group"><label>Anything else they do</label>
                <input type="text" value={vendorEditForm.expertiseOther} onChange={e => setVendorEdit({ expertiseOther: e.target.value })} placeholder="Optional" /></div>

              {!vendorEditForm._id && vendorEditForm.email?.trim() && (
                <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.5, marginBottom: 16 }}>
                  <input type="checkbox" checked={vendorEditForm.sendInvite === true}
                    onChange={e => setVendorEdit({ sendInvite: e.target.checked })} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 13 }}>Email them a link to set their own password. Either way you get the
                    link on screen to send yourself — no password is ever emailed.</span>
                </label>
              )}

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingVendor} onClick={handleSaveVendorEdit}>
                {isSavingVendor ? 'Saving…' : vendorEditForm._id ? 'Save Changes' : 'Add Technician'}
              </button>
              <button className="btn btn-ghost btn-lg" onClick={() => setVendorEditForm(null)}>Cancel</button>
            </div>
          </div>
          <div className="overlay active" onClick={() => setVendorEditForm(null)}></div>
        </>
      )}

      {/* Their way into the portal, after adding them. Reading this out is more
          reliable than an email landing in a spam folder nobody checks. */}
      {!vendorEditForm && lastVendorLink && (
        <>
          <div className="modal active">
            <div className="modal-box">
              <div className="modal-handle"></div>
              <div className="qps-sheet-title">Technician added</div>
              <div className="alert alert-green" style={{ marginBottom: 12, wordBreak: 'break-all', fontSize: 12.5, flexDirection: 'column' }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>Their set-password link — good for 7 days</strong>
                {lastVendorLink}
                <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }}
                  onClick={() => { navigator.clipboard?.writeText(lastVendorLink); showToast('Link copied'); }}>Copy link</button>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 16 }}>
                Their card says <strong>Application pending</strong> until they fill the rest in themselves —
                insurance and licence documents included, which is what decides whether they can be paid.
              </div>
              <button className="btn btn-ghost btn-lg" onClick={() => setLastVendorLink('')}>Done</button>
            </div>
          </div>
          <div className="overlay active" onClick={() => setLastVendorLink('')}></div>
        </>
      )}

      {/* ── Qualifying a lead ───────────────────────────────────
          Agent 8 scores every enquiry as it lands and names what is still
          unanswered. This is where the answers go in, typed while he is on
          the phone to them. The score is worked out again on the server
          from the answers — a band that can be typed means nothing. */}
      {modal === 'qualify' && leadQualify && (() => {
        const lead = activeRequest?.lead || {};
        const open = QUALIFY_QUESTIONS.filter(q => (lead.missing || []).includes(q.key));
        const done = QUALIFY_QUESTIONS.filter(q => !(lead.missing || []).includes(q.key));
        const setQ = (k, v) => setLeadQualify(p2 => ({ ...p2, [k]: v }));
        return (
          <>
            <div className="modal active">
              <div className="modal-box">
                <div className="modal-handle"></div>
                <div className="qps-sheet-title">
                  Qualify this lead
                  {lead.band && (
                    <span className={`tag lead-${lead.band}`} style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                      {lead.band === 'hot' ? 'Hot' : lead.band === 'warm' ? 'Warm' : 'Cold'} · {lead.score}/100
                    </span>
                  )}
                </div>
                <div className="qps-sheet-sub">
                  {activeRequest?.homeownerId?.name || activeRequest?.fullName}
                  {activeRequest?.serviceType ? ` · ${activeRequest.serviceType}` : ''}
                </div>

                {(lead.signals || []).length > 0 && (
                  <div className="alert alert-green" style={{ marginBottom: 14, flexDirection: 'column', alignItems: 'flex-start', fontSize: 12.5 }}>
                    <strong style={{ marginBottom: 4 }}>What it already has going for it</strong>
                    {lead.signals.map((sg, i) => <span key={i}>· {sg}</span>)}
                  </div>
                )}

                {open.length > 0 && (
                  <>
                    <div className="sec-label">Ask them these</div>
                    {open.map(q => (
                      <div className="form-group" key={q.key}>
                        <label>{q.ask}</label>
                        <input
                          type="text"
                          value={leadQualify[q.key] || ''}
                          onChange={e => setQ(q.key, e.target.value)}
                          placeholder="What they said"
                        />
                        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4, lineHeight: 1.45 }}>{q.why}</div>
                      </div>
                    ))}
                  </>
                )}

                {done.length > 0 && (
                  <>
                    <div className="sec-label">Already answered</div>
                    {done.map(q => (
                      <div className="form-group" key={q.key}>
                        <label>{q.label}</label>
                        <input
                          type="text"
                          value={leadQualify[q.key] || ''}
                          onChange={e => setQ(q.key, e.target.value)}
                          placeholder="From the form"
                        />
                      </div>
                    ))}
                  </>
                )}

                <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }}
                  disabled={isSavingLead} onClick={saveLeadQualify}>
                  {isSavingLead ? 'Saving…' : 'Save and re-score'}
                </button>
                <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setLeadQualify(null); }}>Cancel</button>
              </div>
            </div>
            <div className="overlay active" onClick={() => { setModal(null); setLeadQualify(null); }}></div>
          </>
        );
      })()}

      {/* ── What approving an estimate just did ─────────────────
          Three records were written and one of them carries a link that
          lasts seven days. A toast would have flashed past all of it. */}
      {approvedOpened && (
        <>
          <div className="modal active">
            <div className="modal-box">
              <div className="modal-handle"></div>
              <div className="qps-sheet-title">{approvedOpened.clientName} is a client now</div>
              <div className="qps-sheet-sub">
                They approved the estimate, so they have an account and a project of their own.
              </div>

              <div className="alert alert-green" style={{ marginBottom: 12, flexDirection: 'column', alignItems: 'flex-start' }}>
                <strong>{approvedOpened.projectTitle}</strong>
                <span style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                  Open on the Projects tab. Nothing is billed yet — the lines land as you
                  invoice the work, and the statement is what they pay against.
                </span>
              </div>

              {approvedOpened.inviteLink ? (
                <div className="alert alert-green" style={{ marginBottom: 12, wordBreak: 'break-all', fontSize: 12.5, flexDirection: 'column' }}>
                  <strong style={{ display: 'block', marginBottom: 6 }}>Their set-password link — good for 7 days</strong>
                  {approvedOpened.inviteLink}
                  <button className="btn btn-outline btn-sm" style={{ marginTop: 10 }}
                    onClick={() => { navigator.clipboard?.writeText(approvedOpened.inviteLink); showToast('Link copied'); }}>Copy link</button>
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 12 }}>
                  No email on file for them, so there is no portal link to send. Add one on their
                  client page and the link is one tap away.
                </div>
              )}

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }}
                onClick={() => { setApproved(null); setAssignVendorId(''); setModal('assign'); }}>
                Assign a technician
              </button>
              <button className="btn btn-ghost btn-lg" onClick={() => setApproved(null)}>Not yet</button>
            </div>
          </div>
          <div className="overlay active" onClick={() => setApproved(null)}></div>
        </>
      )}

      {/* ── Statement line (admin) ─────────────────────────────── */}
      <div className={`modal ${modal === 'projectLine' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">
            {lineForm._id ? 'Edit Invoice Line' : 'Add Invoice Line'}
          </div>
          <div className="qps-sheet-sub">One trade, one Joist invoice. The client sees this row.</div>

          <div className="form-group"><label>Trade / work <span className="req">*</span></label><input type="text" value={lineForm.trade} onChange={e => setLine({ trade: e.target.value })} placeholder="FRAMING" /></div>
          <div className="form-row">
            <div className="form-group"><label>Invoice #</label><input type="text" value={lineForm.invoiceNumber} onChange={e => setLine({ invoiceNumber: e.target.value })} placeholder="1691" /></div>
            <div className="form-group"><label>Client price <span className="req">*</span></label><input type="number" step="0.01" value={lineForm.amount} onChange={e => setLine({ amount: e.target.value })} placeholder="$0.00" /></div>
          </div>
          {/* The day the work was billed, not the day it is typed in. Without
              this, loading six months of a project put today's date on every
              line of the document the homeowner reads. */}
          <div className="form-group"><label>Invoice date</label>
            <input type="date" value={lineForm.invoiceDate || ''} onChange={e => setLine({ invoiceDate: e.target.value })} />
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              The day this was billed. Set it back when you are catching the statement up on older work.
            </div>
          </div>
          <div className="form-group"><label>Link to the invoice</label><input type="text" value={lineForm.invoiceUrl} onChange={e => setLine({ invoiceUrl: e.target.value })} placeholder="https://…" /></div>

          <div className="form-row">
            <div className="form-group">
              <label>Technician doing it</label>
              <select style={sel} value={lineForm.vendorId} onChange={e => setLine({ vendorId: e.target.value })}>
                <option value="">Not assigned</option>
                {vendors.map(v => <option key={v._id} value={v._id}>{v.businessName}</option>)}
              </select>
            </div>
            <div className="form-group"><label>What it costs you</label><input type="number" step="0.01" value={lineForm.techCost} onChange={e => setLine({ techCost: e.target.value })} placeholder="$0.00" /></div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
            The cost is never shown to the client — it's what makes the profit figure work.
          </div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSaveProjectLine}>{isSavingProject ? 'Saving…' : 'Save Line'}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'projectLine' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Payment from the client (admin) ────────────────────── */}
      <div className={`modal ${modal === 'clientPayment' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">{clientPayForm._id ? 'Correct Payment' : 'Record Client Payment'}</div>
          {clientPayForm._id && (
            <div className="qps-sheet-sub">Every change is written to the project&rsquo;s history.</div>
          )}
          <div className="form-group">
            <label>Toward which invoice</label>
            <select style={sel} value={clientPayForm.lineId} onChange={e => setCPay({ lineId: e.target.value })}>
              <option value="">Spread across the oldest unpaid lines</option>
              {(activeProject?.lines || []).map(l => <option key={l._id} value={l._id}>{l.trade}{l.invoiceNumber ? ` (#${l.invoiceNumber})` : ''}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Amount <span className="req">*</span></label><input type="number" step="0.01" value={clientPayForm.amount} onChange={e => setCPay({ amount: e.target.value })} placeholder="$0.00" /></div>
            <div className="form-group"><label>Date</label><input type="date" value={clientPayForm.date} onChange={e => setCPay({ date: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Form of payment</label>
              <select style={sel} value={clientPayForm.method} onChange={e => setCPay({ method: e.target.value })}>
                {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Reference</label><input type="text" value={clientPayForm.reference} onChange={e => setCPay({ reference: e.target.value })} placeholder="Check 104" /></div>
          </div>
          <button className="btn btn-green btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSaveClientPayment}>
            {isSavingProject ? 'Saving…' : clientPayForm._id ? 'Save Correction' : 'Record Payment'}
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'clientPayment' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Project expense (admin only) ───────────────────────── */}
      <div className={`modal ${modal === 'projectExpense' ? 'active' : ''}`}>
        <div className="modal-box">
          <div className="modal-handle"></div>
          <div className="qps-sheet-title">Log an Expense</div>
          <div className="qps-sheet-sub">Never shown to the client.</div>

          <div className="form-group">
            <label>Which trade</label>
            <select style={sel} value={expenseForm.lineId} onChange={e => setExp({ lineId: e.target.value })}>
              <option value="">Miscellaneous</option>
              {(activeProject?.lines || []).map(l => <option key={l._id} value={l._id}>{l.trade}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Supplier <span className="req">*</span></label><input type="text" value={expenseForm.supplier} onChange={e => setExp({ supplier: e.target.value })} placeholder="Home Depot" /></div>
            <div className="form-group"><label>Amount <span className="req">*</span></label><input type="number" step="0.01" value={expenseForm.amount} onChange={e => setExp({ amount: e.target.value })} placeholder="$0.00" /></div>
          </div>
          {/* Same gap the invoice line sheet had: no date, so everything was
              stamped with the day it was typed. A receipt from April loaded
              in October belongs in April. */}
          <div className="form-group"><label>Date</label>
            <input type="date" value={expenseForm.date || ''} onChange={e => setExp({ date: e.target.value })} /></div>
          <div className="form-group"><label>What was bought</label><input type="text" value={expenseForm.description} onChange={e => setExp({ description: e.target.value })} /></div>
          <div className="form-group"><label>Payment details</label><input type="text" value={expenseForm.paymentDetails} onChange={e => setExp({ paymentDetails: e.target.value })} placeholder="Card ending 4321 / Check 88" /></div>

          <div className="form-group">
            <label>Charge this to a technician?</label>
            <select style={sel} value={expenseForm.chargeToVendorId} onChange={e => setExp({ chargeToVendorId: e.target.value })}>
              <option value="">No — QPS absorbs it</option>
              {vendors.map(v => <option key={v._id} value={v._id}>Deduct from {v.businessName}</option>)}
            </select>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 }}>
              Pick a technician when you fronted supplies for their work. It comes off what you owe them, and they
              see it on their pay breakdown.
            </div>
          </div>

          <div className="form-group">
            <label>Receipt</label>
            <input type="file" accept="image/*,.pdf" onChange={e => setReceiptFile(e.target.files[0] || null)} />
            {receiptFile && <div className="file-chips"><span className="file-chip">{receiptFile.name}</span></div>}
          </div>

          <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }} disabled={isSavingProject} onClick={handleSaveExpense}>{isSavingProject ? 'Saving…' : 'Log Expense'}</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setModal(null)}>Cancel</button>
        </div>
      </div>
      {modal === 'projectExpense' && <div className="overlay active" onClick={() => setModal(null)}></div>}

      {/* ── Loading a project off a spreadsheet ────────────────────── */}
      {/* Two boxes and a Check button. Nothing is written until he has seen
          the three totals and compared them against his own sheet, because
          an import that lands wrong is worse than typing it by hand: it
          looks finished. */}
      {modal === 'projectImport' && importForm && (() => {
        const p = importForm.preview;
        const day = (v) => (v ? new Date(v).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : '—');
        return (
          <div className="modal active">
            <div className="modal-box">
              <div className="modal-handle"></div>
              <div className="qps-sheet-title">Import from a Spreadsheet</div>
              <div className="qps-sheet-sub">{activeProject?.name}</div>

              <div className="imp-step">1. Copy the rows out of your sheet, headers included, and paste them here.</div>

              <div className="form-group">
                <label>Invoice lines</label>
                <textarea
                  rows={6}
                  className="imp-box"
                  value={importForm.linesCsv}
                  onChange={e => setImportForm(f => ({ ...f, linesCsv: e.target.value, preview: null }))}
                  placeholder={'INVOICE DATE\tDESCRIPTION\tINVOICE #\tINVOICE AMOUNT\n4/3/25\tFRAMING\t1659\t$1,237.50'}
                />
                <div className="imp-hint">Needs a description column and an amount column. Invoice number and date are used when they are there.</div>
              </div>

              <div className="form-group">
                <label>Payments received</label>
                <textarea
                  rows={6}
                  className="imp-box"
                  value={importForm.paymentsCsv}
                  onChange={e => setImportForm(f => ({ ...f, paymentsCsv: e.target.value, preview: null }))}
                  placeholder={'INVOICE #\tPAYMENT DATE\tAMOUNT\tFORM OF PAYMENT\tREFERENCE\n1659\t4/18/25\t$1,237.50\tCheck\t111'}
                />
                <div className="imp-hint">Leave this empty if you only want the invoices. Each payment goes on the invoice number it names.</div>
              </div>

              <button
                className="btn btn-outline btn-lg"
                style={{ marginBottom: 12 }}
                disabled={importBusy}
                onClick={runImportPreview}
              >{importBusy ? 'Reading…' : 'Check what this will add'}</button>

              {p && (
                <div className="imp-prev">
                  <div className="imp-step">2. Check these against your sheet.</div>

                  <div className="imp-sums">
                    <div><span>Invoice lines</span><b>{p.lines.count}</b></div>
                    <div><span>Adds up to</span><b>{fmtMoney(p.lines.total)}</b></div>
                    <div><span>Payments</span><b>{p.payments.count}</b></div>
                    <div><span>Adds up to</span><b>{fmtMoney(p.payments.total)}</b></div>
                  </div>

                  <div className="imp-after">
                    <div>Invoiced after this <b>{fmtMoney(p.after.invoiced)}</b></div>
                    <div>Received after this <b>{fmtMoney(p.after.received)}</b></div>
                    <div className={p.after.owed > 0.004 ? 'owed' : 'clear'}>Still owed <b>{fmtMoney(p.after.owed)}</b></div>
                  </div>

                  {!!p.problems.length && (
                    <div className="imp-problems">
                      <div className="imp-problems-head">{p.problems.length} row{p.problems.length === 1 ? '' : 's'} will be left out:</div>
                      <ul>{p.problems.map((m, i) => <li key={i}>{m}</li>)}</ul>
                    </div>
                  )}

                  {!!p.lines.rows.length && (
                    <table className="imp-table">
                      <thead><tr><th>Invoice</th><th>Date</th><th>Description</th><th className="r">Amount</th></tr></thead>
                      <tbody>
                        {p.lines.rows.map((r, i) => (
                          <tr key={i}>
                            <td>{r.invoiceNumber || '—'}</td>
                            <td>{day(r.invoiceDate)}</td>
                            <td className="d">{r.trade}</td>
                            <td className="r">{fmtMoney(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {!!p.payments.rows.length && (
                    <table className="imp-table">
                      <thead><tr><th>Invoice</th><th>Paid</th><th>How</th><th>Ref</th><th className="r">Amount</th></tr></thead>
                      <tbody>
                        {p.payments.rows.map((r, i) => (
                          <tr key={i}>
                            <td>{r.invoiceNumber}</td>
                            <td>{day(r.date)}</td>
                            <td>{r.method || 'Check'}</td>
                            <td>{r.reference || '—'}</td>
                            <td className="r">{fmtMoney(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <div className="imp-step">3. If the totals match, add it to the project.</div>
                  <button
                    className="btn btn-primary btn-lg"
                    style={{ marginBottom: 10 }}
                    disabled={importBusy || (!p.lines.count && !p.payments.count)}
                    onClick={confirmImport}
                  >{importBusy ? 'Adding…' : `Add ${p.lines.count} line${p.lines.count === 1 ? '' : 's'} and ${p.payments.count} payment${p.payments.count === 1 ? '' : 's'}`}</button>
                </div>
              )}

              <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setImportForm(null); }}>Cancel</button>
            </div>
          </div>
        );
      })()}
      {modal === 'projectImport' && <div className="overlay active" onClick={() => { setModal(null); setImportForm(null); }}></div>}

      {/* ── Technician writes an estimate ──────────────────────── */}
      {/* The estimate builder. Full-screen rather than a bottom sheet: it is a
          document, not a question, and the property it is for belongs at the
          top of it. */}
      {modal === 'techEstimate' && (
        <DocSheet
          kind="estimate"
          // What this sheet is depends on what it is holding. "New Estimate"
          // over an estimate QPS already has reads as though the last one went
          // missing.
          title={!estimateForm2.status || estimateForm2.status === 'draft'
            ? t('pj.newEstimate')
            : 'Edit Estimate'}
          number={estimateForm2.estimateNumber}
          onNumberChange={(v) => setEst({ estimateNumber: v })}
          date={estimateForm2.docDate}
          onDateChange={(v) => setDocDate('estimate', v)}
          address={estimateForm2.serviceAddress}
          city={estimateForm2.city} state={estimateForm2.state} zip={estimateForm2.zip}
          cityLine={estimateForm2.cityLine}
          // An estimate against a QPS job is a price TO QPS. Letting a tech
          // type a homeowner's name on it would teach them the wrong thing
          // about whose customer that is, so it is stated and not editable.
          clientFixed={estimateForm2.requestId ? 'QPS' : undefined}
          client={estimateForm2.customer}
          onEditClient={estimateForm2.requestId ? undefined : () => openDocClient('estimate')}
          items={estimateForm2.items}
          total={estimateItemsTotal}
          onOpenItem={setEditingItem}
          onAddItem={() => {
            setEst({ items: [...estimateForm2.items, { name: '', description: '', kind: 'labor', qty: 1, unitCost: '', photos: [], files: [] }] });
            setEditingItem(estimateForm2.items.length);
          }}
          onAddressChange={(a) => setEst({ serviceAddress: a.address, city: a.city, state: a.state, zip: a.zip })}
          onDeleteItem={(i) => {
            // A line carries photos taken on site and a price someone worked
            // out. Deleting one on a mis-swipe should cost a tap, not the work.
            const row = estimateForm2.items[i];
            const name = row?.name || row?.description?.split('\n')[0] || `line ${i + 1}`;
            if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
            setEst({ items: estimateForm2.items.filter((_, idx) => idx !== i) });
          }}
          onCancel={() => setModal(null)}
          onDone={() => handleSaveTechEstimate('draft')}
          doneLabel="Save"
          // Sending is for something QPS has not been shown yet, or has sent
          // back. An estimate already sitting in their queue is updated by
          // saving it — a second "Send for approval" on the same document
          // would suggest the first one did not land.
          onSend={['submitted', 'approved'].includes(estimateForm2.status)
            ? undefined
            : handleSubmitTechEstimate}
          sendLabel={estimateForm2.status === 'rejected' ? 'Send again' : 'Send for approval'}
          busy={isSavingProject}
        >
          {/* Notes only. Service Type is carried in from the job that opened
              this estimate and never needed retyping; the address is edited in
              the banner, where it is read. */}
          <div className="qd-extra">
            {/* No placeholder. The old one read like something already typed,
                and a tech scrolling past it had to decide whether it was their
                own note or ours. */}
            <div className="form-group">
              <label>Notes</label>
              <textarea value={estimateForm2.notes} onChange={e => setEst({ notes: e.target.value })} />
            </div>

            <DocExtras
              url={estimateForm2.externalUrl}
              onUrl={v => setEst({ externalUrl: v })}
              files={estimateForm2.documents}
              onFiles={v => setEst({ documents: v })}
              kind="estimate"
            />
          </div>
        </DocSheet>
      )}

      {/* ── Recording a DOPL licence check (admin) ─────────────── */}
      {/* Deliberately plain. It is a form for writing down what you just read
          on somebody else's website, so it asks exactly that and nothing more. */}
      {modal === 'licenseCheck' && licenseForm && (
        <>
          <div className="modal active">
            <div className="modal-box">
              <div className="modal-handle"></div>
              <div className="qps-sheet-title">Licence check</div>
              <div className="qps-sheet-sub">
                {licenseForm.businessName} · look the number up on Utah DOPL, then write down what it said.
              </div>

              <div className="form-group">
                <label>Licence number</label>
                <input type="text" value={licenseForm.licenseNumber}
                  onChange={e => setLicenseForm(p => ({ ...p, licenseNumber: e.target.value }))}
                  placeholder="As it appears on DOPL" />
              </div>

              <div className="form-group">
                <label>What DOPL showed <span className="req">*</span></label>
                <select style={sel} value={licenseForm.result}
                  onChange={e => setLicenseForm(p => ({ ...p, result: e.target.value }))}>
                  <option value="">Select…</option>
                  <option value="active">Active and in good standing</option>
                  <option value="expired">Expired</option>
                  <option value="suspended">Suspended or revoked</option>
                  <option value="not-found">No licence found under that number</option>
                  <option value="name-mismatch">Found, but a different name than the business</option>
                  <option value="not-required">No licence needed for the work they take</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Licence type</label>
                  <input type="text" value={licenseForm.licenseType}
                    onChange={e => setLicenseForm(p => ({ ...p, licenseType: e.target.value }))}
                    placeholder="General Contractor" />
                </div>
                <div className="form-group">
                  <label>Expires</label>
                  <input type="date" value={licenseForm.licenseExpiresAt}
                    onChange={e => setLicenseForm(p => ({ ...p, licenseExpiresAt: e.target.value }))} />
                </div>
              </div>

              <div className="form-group">
                <label>Note</label>
                <textarea value={licenseForm.note}
                  onChange={e => setLicenseForm(p => ({ ...p, note: e.target.value }))}
                  placeholder="Anything that does not fit above" />
              </div>

              <div style={{ fontSize: 12, color: 'var(--muted)', margin: '-4px 0 12px', lineHeight: 1.5 }}>
                Saved with today's date and your name on it. Checking again writes a new record rather than
                changing this one.
              </div>

              <button className="btn btn-primary btn-lg" style={{ marginBottom: 10 }}
                disabled={isSubmittingApp} onClick={handleSaveLicenseCheck}>
                {isSubmittingApp ? 'Saving…' : 'Record this check'}
              </button>
              <button className="btn btn-ghost btn-lg" onClick={() => { setModal(null); setLicenseForm(null); }}>Cancel</button>
            </div>
          </div>
          <div className="overlay active" onClick={() => { setModal(null); setLicenseForm(null); }}></div>
        </>
      )}

      {/* ── Technician writes an invoice ──────────────────────── */}
      {/* The estimate builder, with the word changed. Same banner, same lines,
          same Add Item, same totals, same notes, link and attachments —
          because a technician who has learned one has learned both. */}
      {modal === 'techInvoice' && (
        <DocSheet
          kind="invoice"
          title={invoiceForm2._id ? 'Edit Invoice' : 'New Invoice'}
          number={invoiceForm2.invoiceNumber}
          onNumberChange={(v) => setInv2({ invoiceNumber: v })}
          date={invoiceForm2.docDate}
          onDateChange={(v) => setDocDate('invoice', v)}
          address={invoiceForm2.serviceAddress}
          city={invoiceForm2.city} state={invoiceForm2.state} zip={invoiceForm2.zip}
          cityLine={invoiceForm2.cityLine}
          clientFixed={invoiceForm2.requestId ? 'QPS' : undefined}
          client={invoiceForm2.customer}
          onEditClient={invoiceForm2.requestId ? undefined : () => openDocClient('invoice')}
          items={invoiceForm2.items}
          total={invoiceItemsTotal}
          onOpenItem={setEditingInvoiceItem}
          onAddItem={() => {
            setInv2({ items: [...invoiceForm2.items, { name: '', description: '', kind: 'labor', qty: 1, unitCost: '', photos: [], files: [] }] });
            setEditingInvoiceItem(invoiceForm2.items.length);
          }}
          onAddressChange={(a) => setInv2({ serviceAddress: a.address, city: a.city, state: a.state, zip: a.zip })}
          onDeleteItem={(i) => {
            const row = invoiceForm2.items[i];
            const name = row?.name || row?.description?.split('\n')[0] || `line ${i + 1}`;
            if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
            setInv2({ items: invoiceForm2.items.filter((_, idx) => idx !== i) });
          }}
          onCancel={() => setModal(null)}
          onSend={handleSaveTechInvoice}
          sendLabel={invoiceForm2._id ? 'Save invoice' : 'Send invoice'}
          busy={isSavingLedger}
        >
          <div className="qd-extra">
            <div className="form-group">
              <label>Notes</label>
              <textarea value={invoiceForm2.notes} onChange={e => setInv2({ notes: e.target.value })} />
            </div>

            <DocExtras
              url={invoiceForm2.externalUrl}
              onUrl={v => setInv2({ externalUrl: v })}
              files={invoiceForm2.documents}
              onFiles={v => setInv2({ documents: v })}
              kind="invoice"
            />
          </div>
        </DocSheet>
      )}

      {/* One line of the invoice, on the same screen the estimate uses. */}
      {modal === 'techInvoice' && editingInvoiceItem !== null && invoiceForm2.items[editingInvoiceItem] && (
        <ItemEditor
          item={invoiceForm2.items[editingInvoiceItem]}
          index={editingInvoiceItem}
          total={invoiceForm2.items.length}
          onChange={(next) => {
            const items = [...invoiceForm2.items];
            items[editingInvoiceItem] = next;
            setInv2({ items });
          }}
          onDelete={() => {
            setInv2({ items: invoiceForm2.items.filter((_, i) => i !== editingInvoiceItem) });
            setEditingInvoiceItem(null);
          }}
          onClose={() => setEditingInvoiceItem(null)}
        />
      )}

      {/* One line of the estimate, on its own screen (see components/ItemEditor). */}
      {modal === 'techEstimate' && editingItem !== null && estimateForm2.items[editingItem] && (
        <ItemEditor
          item={estimateForm2.items[editingItem]}
          index={editingItem}
          total={estimateForm2.items.length}
          onChange={(next) => {
            const items = [...estimateForm2.items];
            items[editingItem] = next;
            setEst({ items });
          }}
          onDelete={() => {
            const items = estimateForm2.items.filter((_, i) => i !== editingItem);
            // Removing the last line leaves the sheet empty, which is what the
            // empty state is for. Re-seeding a blank one here put back the
            // "Untitled line" the tech had just deleted.
            setEst({ items });
            setEditingItem(null);
          }}
          onClose={() => setEditingItem(null)}
        />
      )}
    </>
  );
}
